import React, {useId} from 'react'
import {Icon, chakra} from '@chakra-ui/react'
import {keyframes} from '@emotion/react'
import {defineMessage, useIntl} from 'react-intl'
import {useFormatMessage} from '../../hooks/use-format-message'
import PropTypes from 'prop-types'

const spin = keyframes`
  from {transform: rotate(0deg);}
  to {transform: rotate(360deg)}
`

export const DEFAULT_ICON_META = {
    Alert2Icon: {
        title: defineMessage({
            defaultMessage: 'Alert icon',
            id: 'alert.icon.svg.title.default'
        }),
        description: defineMessage({
            defaultMessage: 'Alert icon',
            id: 'alert.icon.svg.description.default'
        })
    },
    CloseIcon: {
        title: defineMessage({
            defaultMessage: 'Close icon',
            id: 'close.icon.svg.title.default'
        }),
        description: defineMessage({
            defaultMessage: 'Close icon',
            id: 'close.icon.svg.description.default'
        })
    },
    SuccessIcon: {
        title: defineMessage({
            defaultMessage: 'Success icon',
            id: 'success.icon.svg.title.default'
        }),
        description: defineMessage({
            defaultMessage: 'Success icon',
            id: 'success.icon.svg.description.default'
        })
    },
    RadioIcon: {
        title: defineMessage({
            defaultMessage: 'Radio icon',
            id: 'radio.icon.svg.title.default'
        }),
        description: defineMessage({
            defaultMessage: 'Radio icon',
            id: 'radio.icon.svg.description.default'
        })
    },
    CheckedRadioIcon: {
        title: defineMessage({
            defaultMessage: 'Checked radio icon',
            id: 'checked.radio.icon.svg.title.default'
        }),
        description: defineMessage({
            defaultMessage: 'Checked radio icon',
            id: 'checked.radio.icon.svg.description.default'
        })
    }
}

export const RadioIcon = ({svgTitle, svgDescription, ...props}) => {
    const defaultTitle = useFormatMessage(DEFAULT_ICON_META.RadioIcon.title)
    const defaultDescription = useFormatMessage(DEFAULT_ICON_META.RadioIcon.description)
    const title = svgTitle || defaultTitle
    const description = svgDescription || defaultDescription

    return (
        <Icon viewBox="0 0 13 12" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <rect
                x="1"
                y="0.498047"
                width="11.0039"
                height="11.0039"
                rx="5.50195"
                stroke="#5E5E5E"
                fill="white"
            />
        </Icon>
    )
}

export const CheckedRadioIcon = ({svgTitle, svgDescription, ...props}) => {
    const defaultTitle = useFormatMessage(DEFAULT_ICON_META.RadioIcon.title)
    const defaultDescription = useFormatMessage(DEFAULT_ICON_META.RadioIcon.description)
    const title = svgTitle || defaultTitle
    const description = svgDescription || defaultDescription

    return (
        <Icon viewBox="0 0 13 12" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <rect
                x="1.03125"
                y="0.498047"
                width="11.0039"
                height="11.0039"
                rx="5.50195"
                stroke="black"
                fill="white"
            />
            <rect
                x="2.53125"
                y="1.99805"
                width="8.00391"
                height="8.00391"
                rx="4.00195"
                fill="black"
            />
        </Icon>
    )
}

export const AlertIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 16 16" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" strokeLinecap="square" strokeWidth="1.3" d="M8 2.35v7.6" />
            <path fill="currentColor" d="M7.35 11.95h1.3v1.3h-1.3z" />
        </Icon>
    )
}

export const Alert2Icon = ({svgTitle, svgDescription, insideColor, ...props}) => {
    // Use the useFormatMessage hook to get the default title and description if intl avaliable
    const defaultTitle = useFormatMessage(DEFAULT_ICON_META.Alert2Icon.title)
    const defaultDescription = useFormatMessage(DEFAULT_ICON_META.Alert2Icon.description)

    // Use provided svgTitle and svgDescription or fall back to defaults
    const title = svgTitle || defaultTitle
    const description = svgDescription || defaultDescription

    return (
        <Icon viewBox="0 0 16 16" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <circle cx="8" cy="8" r="8" fill="currentColor" />
            <path
                stroke={insideColor || '#fff'}
                strokeLinecap="square"
                strokeWidth="1.3"
                d="M8 3v7.4"
            />
            <path
                fill="#000"
                stroke={insideColor || '#fff'}
                strokeWidth=".65"
                d="M7.675 12.725h.65v.65h-.65z"
            />
        </Icon>
    )
}

export const BasketIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 13" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" strokeWidth="1.1" d="M.549 4.637h10.9v7.813H.549z" />
            <path
                stroke="currentColor"
                strokeWidth="1.1"
                d="M3.78 4.807v-1.98c0-2.45 4.42-2.422 4.42 0v1.98"
            />
        </Icon>
    )
}

export const BrandLogo = ({svgTitle, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Vilebrequin logo',
            id: 'logo.svg.title'
        })

    return (
        <Icon viewBox="0 0 94 36" fill="none" {...props}>
            <title>{title}</title>
            <g clipPath="url(#a)">
                <path fill="#fff" d="M93.073.927H.93V35.07h92.143V.927Z" />
                <path fill="#143A64" d="M0 0v36h94V0H0Zm92.14 1.856v32.29H1.858V1.856H92.14Z" />
                <path fill="#143A64" d="M91.095 2.898H2.905V33.1h88.19V2.9Z" />
                <path
                    fill="#fff"
                    d="M9.733 20.798 7.477 5.958H5.259L9.11 31.4c.354-.268.716-.533 1.088-.795l4.192-24.648h-2.171l-2.485 14.84ZM71.505 5.911v13.775a.745.745 0 0 1-.748.743h-.01l-.974-.254c-.41-.088-.729-.358-.742-.757v.114l-.003-13.618h-2.169V19.5l.006.218v.099c0 1.23.865 2.179 2.106 2.482l2.517.684c1.239 0 2.24-.924 2.24-2.16V5.911h-2.223Zm-32.97.041c-.027 0-.052-.003-.077-.003h-4.421v15.208s1.582-.315 4.593-.703c1.157-.088 2.068-.97 2.068-2.15V14.86c0-.9-.538-1.67-1.304-2.026a2.233 2.233 0 0 0 1.304-2.012V8.164a2.24 2.24 0 0 0-2.166-2.215l.003.003Zm.199 11.658c0 .41-.3.692-.748.752l-1.786.205v-4.645h1.786v.008c.385 0 .702.296.74.67h.008v3.01Zm0-6.594c0 .41-.336.74-.748.743v.009H36.2v-3.64h1.786v.009c.388 0 .702.295.74.67h.008v2.209ZM22.419 5.952h-2.182v19.215a63.428 63.428 0 0 1 5.39-1.938v-2.321c-1.743.503-3.208 1.055-3.208 1.055V5.95v.003Zm6.476 9.042 2.275-.197v-2.185l-2.275.069V8.123h3.246v-2.17h-5.387v16.934a73.915 73.915 0 0 1 5.39-1.35v-2.259c-1.394.287-3.246.736-3.246.736v-5.02h-.003Zm46.682 9.329c.748.276 1.481.563 2.201.86V5.942h-2.2v18.382ZM86.401 5.949v17.075l-4.553-17.04v-.007l-.008-.028h-2.188v20.047a55.86 55.86 0 0 1 2.188 1.053V14.256l.008.032v-.03l3.977 15.049a42.535 42.535 0 0 1 2.758 1.892V5.949h-2.185.003Zm-23.565-.013h-2.395a2.238 2.238 0 0 0-2.231 2.236v10.125c0 1.233.952 2.124 2.176 2.302l.273.04.066.012v.872c0 1.074.829 1.85 1.895 2.004l.238.035.385.044v-1.799c-.579 0-.612-.508-.625-.85v-.014l.218.033c1.238 0 2.242-.828 2.242-2.064V8.159a2.24 2.24 0 0 0-2.24-2.223h-.002Zm.025 11.869c0 .41-.333.743-.745.743l-1.015-.145c-.365-.049-.704-.369-.717-.757v-.18l-.003-8.654h.005a.747.747 0 0 1 .732-.716h.995c.41 0 .74.328.748.732v8.98-.003Zm-46.988 9.372c.709-.366 1.44-.722 2.185-1.064V5.95h-2.185v21.228Zm35.367-7.325c1.817.05 3.61.15 5.371.31v-2.248l-3.254-.216v-3.839h2.18v-2.086h-2.18V8.126h3.257V5.949h-5.37v13.9l-.004.003ZM47.182 5.955c-.028 0-.055-.003-.077-.003h-4.419v14.103a92.58 92.58 0 0 1 2.16-.132V13.89h1.787v.009c.388 0 .699.295.74.67h.008v5.273a77.971 77.971 0 0 1 1.966-.016v-4.96c0-.9-.537-1.67-1.303-2.026a2.233 2.233 0 0 0 1.303-2.012V8.17a2.239 2.239 0 0 0-2.163-2.215h-.002Zm.199 5.06c0 .41-.336.742-.748.744v.009h-1.786v-3.64h1.786v.009c.388 0 .699.295.74.67h.008v2.209Z"
                />
            </g>
            <defs>
                <clipPath id="a">
                    <path fill="#fff" d="M0 0h94v36H0z" />
                </clipPath>
            </defs>
        </Icon>
    )
}

export const BrandLogoWhite = ({svgTitle, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Vilebrequin logo',
            id: 'logo.svg.title'
        })

    return (
        <Icon viewBox="0 0 94 36" fill="none" {...props}>
            <title>{title}</title>
            <g fill="#fff" clipPath="url(#a)">
                <path d="M.383.495v36h94v-36h-94Zm92.14 1.857v32.29H2.24V2.351h90.282Z" />
                <path d="M10.115 21.294 7.86 6.453H5.642L9.49 31.896a43.34 43.34 0 0 1 1.088-.795l4.192-24.648h-2.17l-2.486 14.84ZM71.887 6.406v13.775a.745.745 0 0 1-.747.743h-.011l-.974-.254c-.409-.087-.728-.358-.742-.757v.115l-.002-13.619h-2.169v13.586l.005.218v.099c0 1.23.865 2.179 2.106 2.482l2.518.684c1.238 0 2.24-.924 2.24-2.16V6.406H71.886Zm-32.97.041-.076-.002h-4.422v15.207s1.582-.315 4.594-.703c1.156-.088 2.067-.97 2.067-2.149v-3.445c0-.9-.537-1.67-1.303-2.026a2.233 2.233 0 0 0 1.303-2.012V8.659a2.24 2.24 0 0 0-2.165-2.214l.002.002Zm.2 11.659c0 .41-.3.691-.748.751l-1.787.205v-4.645h1.787v.008c.385 0 .701.296.74.67h.007v3.01Zm0-6.595c0 .41-.336.74-.748.744v.008h-1.787v-3.64h1.787v.009c.387 0 .701.295.74.67h.007v2.209ZM22.801 6.447H20.62v19.216a63.438 63.438 0 0 1 5.39-1.939v-2.321c-1.743.503-3.208 1.055-3.208 1.055V6.445v.002Zm6.476 9.042 2.274-.197v-2.184l-2.274.068V8.618h3.245v-2.17h-5.387v16.934a73.966 73.966 0 0 1 5.39-1.35v-2.259c-1.394.287-3.246.736-3.246.736v-5.02h-.002Zm46.682 9.329c.747.276 1.48.563 2.201.861V6.436H75.96v18.382ZM86.783 6.445v17.074L82.231 6.48v-.008l-.008-.027h-2.188V26.49c.745.339 1.473.689 2.188 1.053V14.75l.008.033v-.03l3.977 15.048a42.55 42.55 0 0 1 2.758 1.892V6.444H86.78h.002ZM63.22 6.43h-2.395a2.238 2.238 0 0 0-2.232 2.236v10.125c0 1.233.952 2.124 2.177 2.302l.273.04.065.012v.872c0 1.075.83 1.851 1.896 2.004l.237.036.385.043v-1.799c-.578 0-.611-.508-.625-.85v-.014l.219.033c1.238 0 2.242-.828 2.242-2.064V8.654a2.24 2.24 0 0 0-2.24-2.223h-.002Zm.024 11.869c0 .41-.333.743-.745.743l-1.014-.145c-.366-.049-.704-.369-.718-.757v-.18l-.002-8.654h.005a.747.747 0 0 1 .731-.716h.996c.409 0 .739.328.747.733v8.978-.002Zm-46.988 9.372c.71-.366 1.44-.722 2.185-1.063V6.445h-2.185v21.227Zm35.368-7.325c1.817.05 3.609.15 5.37.31v-2.248l-3.253-.216v-3.839h2.18v-2.086h-2.18V8.621h3.257V6.445h-5.371v13.9l-.003.003ZM47.564 6.45c-.027 0-.054-.003-.076-.003h-4.42V20.55a92.037 92.037 0 0 1 2.161-.131v-6.035h1.787v.009c.387 0 .698.295.74.67h.007v5.274a77.954 77.954 0 0 1 1.967-.017v-4.959c0-.9-.537-1.671-1.304-2.027a2.233 2.233 0 0 0 1.304-2.012V8.665a2.239 2.239 0 0 0-2.163-2.215h-.003Zm.2 5.06c0 .41-.336.742-.748.745v.008h-1.787v-3.64h1.787v.009c.387 0 .698.295.74.67h.007v2.209Z" />
            </g>
            <defs>
                <clipPath id="a">
                    <path fill="#fff" d="M.383.495h94v36h-94z" />
                </clipPath>
            </defs>
        </Icon>
    )
}

export const BookIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <g clipPath="url(#a)">
                <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="1.1"
                    d="M.999 1.5h3a2 2 0 0 1 2 2v7a1.5 1.5 0 0 0-1.5-1.5h-3.5V1.5Z"
                />
                <path
                    fill="currentColor"
                    d="M10.998 1.5h.55V.95h-.55v.55Zm-3 0V.95v.55Zm-2 2h-.55.55Zm-.55 7a.55.55 0 1 0 1.1 0h-1.1ZM7.499 9v-.55V9Zm3.5 0v.55h.55V9h-.55Zm0-8.05h-3v1.1h3V.95Zm-3 0a2.55 2.55 0 0 0-1.803.747l.778.778A1.45 1.45 0 0 1 8 2.05V.95Zm-1.803.747A2.55 2.55 0 0 0 5.45 3.5h1.1c0-.385.152-.753.424-1.025l-.778-.778ZM5.45 3.5v7h1.1v-7h-1.1Zm1.1 7c0-.252.1-.494.278-.672l-.778-.778a2.05 2.05 0 0 0-.6 1.45h1.1Zm.278-.672a.95.95 0 0 1 .672-.278v-1.1a2.05 2.05 0 0 0-1.45.6l.778.778Zm.672-.278h3.5v-1.1h-3.5v1.1Zm4.05-.55V1.5h-1.1V9h1.1Z"
                />
            </g>
            <defs>
                <clipPath id="a">
                    <path fill="#fff" d="M-.001 0h12v12h-12z" />
                </clipPath>
            </defs>
        </Icon>
    )
}

export const CheckIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 16 16" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                d="m13.611 3.611-7.945 7.946-3.277-3.28-.778.778 4.055 4.056 8.723-8.722Z"
            />
        </Icon>
    )
}

export const ChevronUpIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" strokeWidth="1.1" d="M10.833 8.175 6.166 3.51 1.5 8.175" />
        </Icon>
    )
}

export const ChevronDownIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" strokeWidth="1.1" d="m1.5 3.509 4.666 4.667 4.667-4.667" />
        </Icon>
    )
}

export const ChevronLeftIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" strokeWidth="1.1" d="M8.5 1.509 3.832 6.175l4.666 4.667" />
        </Icon>
    )
}

export const ChevronRightIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" strokeWidth="1.1" d="M3.833 10.842 8.5 6.175 3.833 1.51" />
        </Icon>
    )
}

export const CloseIcon = ({svgTitle, svgDescription, ...props}) => {
    // Use the useFormatMessage hook to get the default title and description if intl avaliable
    const defaultTitle = useFormatMessage(DEFAULT_ICON_META.CloseIcon.title)
    const defaultDescription = useFormatMessage(DEFAULT_ICON_META.CloseIcon.description)

    // Use provided svgTitle and svgDescription or fall back to defaults
    const title = svgTitle || defaultTitle
    const description = svgDescription || defaultDescription

    return (
        <Icon viewBox="0 0 14 14" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" strokeWidth="1.3" d="M12.998 1 1 13M1.001 1 13 13" />
        </Icon>
    )
}

export const CubeIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 14" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.1"
                d="m5.999 1.5 5 2.5v6l-5 2.5-5-2.5V4l5-2.5Z"
                clipRule="evenodd"
            />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.1"
                d="m1.158 4.08 4.84 2.42 4.84-2.42M5.999 12.38V6.5"
            />
        </Icon>
    )
}

export const CustomerServiceIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 34 34" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                d="M28.536 19.547a5.569 5.569 0 0 0-3.95-1.636c-1.542 0-2.94.625-3.95 1.636A5.568 5.568 0 0 0 19 23.497c0 1.542.625 2.939 1.636 3.95a5.569 5.569 0 0 0 3.95 1.636c1.543 0 2.94-.625 3.95-1.636a5.568 5.568 0 0 0 1.636-3.95 5.569 5.569 0 0 0-1.636-3.95Zm-3.97 1.73a.85.85 0 0 0-.58.248.79.79 0 0 0-.237.562h-.002v.019c.014.602.528.754 1.032.904.882.261 1.746.518 1.73 1.873a1.947 1.947 0 0 1-.567 1.359h-.002c-.23.23-.517.401-.838.491l.004.551a.542.542 0 0 1-1.084.009l-.004-.575a1.922 1.922 0 0 1-1.356-1.84.544.544 0 0 1 1.088 0 .834.834 0 0 0 .837.838c.23 0 .437-.092.587-.242a.858.858 0 0 0 .252-.599c.006-.538-.468-.68-.952-.823-.881-.261-1.779-.528-1.81-1.924v-.037l-.003-.005c0-.52.215-.988.56-1.33a1.94 1.94 0 0 1 .797-.478l-.004-.567a.542.542 0 1 1 1.084-.009l.004.56c.323.09.612.262.843.493l.002.003c.348.348.564.828.564 1.359a.544.544 0 0 1-1.088 0 .836.836 0 0 0-.837-.837v-.003h-.02Zm5.26-3.02a7.388 7.388 0 0 0-5.24-2.17c-2.046 0-3.899.83-5.24 2.17a7.388 7.388 0 0 0-2.17 5.24c0 2.047.83 3.899 2.17 5.24a7.387 7.387 0 0 0 5.24 2.17c2.046 0 3.899-.83 5.24-2.17a7.386 7.386 0 0 0 2.17-5.24c0-2.046-.83-3.899-2.17-5.24Zm-.52 9.96a6.654 6.654 0 0 1-4.72 1.954 6.653 6.653 0 0 1-4.719-1.954 6.655 6.655 0 0 1-1.955-4.72c0-1.843.747-3.512 1.955-4.72a6.652 6.652 0 0 1 4.72-1.954c1.842 0 3.511.747 4.719 1.955a6.654 6.654 0 0 1 1.954 4.72 6.654 6.654 0 0 1-1.954 4.718Zm-14.32-4.295a.497.497 0 0 1 0-.995h1.385a8.255 8.255 0 0 1 2.402-5.217 8.257 8.257 0 0 1 6.327-2.412V13.36c0-.291-.119-.556-.31-.747a1.054 1.054 0 0 0-.747-.31h-2.904l4.283-2.683 3.184 5.083c.009.014.01.032.007.05a.062.062 0 0 1-.027.04l-1.416.887a8.3 8.3 0 0 1 1.13.46l.81-.508a1.052 1.052 0 0 0 .335-1.454L22.811 3.59a1.053 1.053 0 0 0-1.454-.335l-14.445 9.05h-3.85c-.29 0-.554.12-.747.31-.192.191-.311.455-.311.746v12.498c0 .292.119.556.31.747h.001c.193.191.457.31.746.31h13.991a8.243 8.243 0 0 1-.698-2.993h-1.367Zm6.895-19.829a.066.066 0 0 1 .05-.006.061.061 0 0 1 .04.026l1.557 2.485-9.107 5.706H8.776l13.105-8.21ZM7.493 23.923H5.772a.497.497 0 0 1 0-.996h1.721a.497.497 0 1 1 0 .995Zm1.88-5.349a.497.497 0 0 1-.497.498H5.117a.497.497 0 0 1-.497-.498v-3.3c0-.275.223-.497.497-.497h3.759c.274 0 .497.222.497.497v3.3Zm2.734 5.348h-1.722a.497.497 0 0 1 0-.995h1.722a.497.497 0 0 1 0 .995Zm.18-7.09c0-.275.222-.497.497-.497h4.457a.497.497 0 0 1 0 .995h-4.457a.498.498 0 0 1-.497-.498Zm-6.672-1.06h2.763v2.305H5.615V15.77Z"
            />
        </Icon>
    )
}

/**
 * DeliveryIcon component renders a customizable SVG icon representing delivery.
 *
 * This component utilizes the `useIntl` hook to provide internationalization support
 * for the title and description of the SVG, allowing for localization of these attributes.
 * If `svgTitle` or `svgDescription` are not provided, default messages are used.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.svgTitle] - Optional title for the SVG element. If not provided, a default title is used.
 * @param {string} [props.svgDescription] - Optional description for the SVG element. If not provided, a default description is used.
 * @param {Object} props.rest - Additional properties to be spread onto the SVG element.
 *
 * @returns {JSX.Element} A JSX element representing the delivery icon.
 */
export const DeliveryIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 32" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="#000"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.4"
                d="m14.393 10.078 9 5.15M27.893 13.809a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4a2 2 0 0 0-1 1.73v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.73v-8Z"
            />
            <path
                stroke="#000"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.4"
                d="m10.192 12.809 8.7 5 8.7-5M18.893 27.809v-10"
            />
            <path
                stroke="#000"
                strokeLinecap="round"
                strokeWidth="1.4"
                d="m8.176 6.174 3.433 1.982M13.074 4.191l3.434 1.983M4.107 9.338l3.434 1.982M4.107 14.32l3.434 1.982M4.107 19.303l3.434 1.982"
            />
        </Icon>
    )
}

export const EasyReturnsIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 34 34" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                fillRule="evenodd"
                d="m4.672 14.563-2.65 11.68c1.612.779 3.355 1.28 5.228 1.52 1.928.26 4.004.223 6.192-.037l3.096-5.821V8.889h-6.1c-.11 1.502-.778 2.856-1.78 3.875a6.128 6.128 0 0 1-3.986 1.799Zm19.801-5.674a5.156 5.156 0 0 0 1.502 3.19 5.256 5.256 0 0 0 3.17 1.501L28.09 8.89h-3.616ZM9.975 7.907h17.928V6.053H6.137v1.854h3.838Zm7.527.982v13.016l3.097 5.821c2.187.26 4.264.297 6.192.037 1.872-.24 3.615-.741 5.228-1.52l-2.651-11.68a6.143 6.143 0 0 1-4.079-1.799 6.21 6.21 0 0 1-1.798-3.875h-5.989ZM4.895 13.562c1.205-.13 2.262-.686 3.078-1.484A5.157 5.157 0 0 0 9.474 8.89H5.952l-1.057 4.673Z"
                clipRule="evenodd"
            />
        </Icon>
    )
}

export const FilterIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <g clipPath="url(#a)">
                <path
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                    strokeWidth="1.1"
                    d="M10.498 2h-3.5m-2 0h-3.5m9 4h-4.5m-2 0h-2.5m9 4h-2.5m-2 0h-4.5m5.5-9v2m-3 2v2m4 2v2"
                />
            </g>
            <defs>
                <clipPath id="a">
                    <path fill="#fff" d="M-.001 0h12v12h-12z" />
                </clipPath>
            </defs>
        </Icon>
    )
}

/**
 * Filter2Icon is a React functional component that renders a customizable SVG icon.
 * This component utilizes the `useIntl` hook from `react-intl` to provide internationalization
 * support for the icon's title and description, allowing for localization of these attributes.
 *
 * @param {Object} props - The properties object passed to the component.
 * @param {string} [props.svgTitle] - Optional title for the SVG element. If not provided,
 *                                    a default title will be used.
 * @param {string} [props.svgDescription] - Optional description for the SVG element. If not provided,
 *                                          a default description will be used.
 * @param {Object} [props.rest] - Additional properties that can be passed to the SVG element.
 *
 * @returns {JSX.Element} A JSX element representing the SVG icon with a title and description.
 *
 * @example
 * <Filter2Icon svgTitle="Custom Title" svgDescription="Custom Description" />
 *
 * @component
 */
export const Filter2Icon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 15 11" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeWidth="1.303"
                d="M0 2.985h5.297M9.172 2.985h3.389M5.76 1.439h3.092v3.092H5.76zM12.56 8.683H7.264M3.389 8.683h-3.39M6.8 7.137H3.71v3.092H6.8z"
            />
        </Icon>
    )
}

export const FlagFRIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 22" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <g clipPath="url(#clip0_4040_419)">
                <path d="M0 0.176071H32V21.8234H0V0.176071Z" fill="white" />
                <path d="M0 0.176071H10.6664V21.8234H0V0.176071Z" fill="#0052B4" />
                <path d="M21.3335 0.176338H31.9999V21.8237H21.3335V0.176338Z" fill="#D80027" />
            </g>
            <defs>
                <clipPath id="clip0_4040_419">
                    <rect
                        width="32"
                        height="21.6471"
                        fill="white"
                        transform="translate(0 0.176468)"
                    />
                </clipPath>
            </defs>
        </Icon>
    )
}

export const FlagUSIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 55.2 38.4" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="#b22234"
                d="M3.03 0h49.13c1.67 0 3.03 1.36 3.03 3.03v32.33c0 1.67-1.36 3.03-3.03 3.03H3.03A3.02 3.02 0 0 1 0 35.37V3.03C0 1.36 1.36 0 3.03 0z"
            />
            <path
                fill="#fff"
                d="M.02 2.73h55.17c.01.1.02.2.02.31v2.94H0V3.03c0-.1.01-.2.02-.3zM55.2 8.67v3.24H0V8.67h55.2zm0 5.94v3.24H0v-3.24h55.2zm0 5.94v3.24H0v-3.24h55.2zm0 5.94v3.24H0v-3.24h55.2zm0 5.94v2.93c0 .1-.01.21-.02.31H.02c-.01-.09-.02-.2-.02-.3v-2.93h55.2v-.01z"
            />
            <path d="M20.8 0v20.68H0V3.03C0 1.36 1.36 0 3.03 0H20.8z" fill="#3c3b6e" />
            <path
                fill="#fff"
                d="m1.23 2.86.69 2.15L.1 3.68h2.26L.53 5.01l.7-2.15zM1.23 7.02l.69 2.15L.1 7.84h2.26L.53 9.17l.7-2.15zM1.23 11.18l.69 2.15L.1 12h2.26L.53 13.33l.7-2.15zM1.23 15.34l.69 2.15L.1 16.16h2.26L.53 17.49l.7-2.15zM3.67.78l.7 2.15L2.54 1.6h2.27L2.97 2.93l.7-2.15zM3.67 4.94l.7 2.15-1.83-1.33h2.27L2.97 7.09l.7-2.15zM3.67 9.1l.7 2.15-1.83-1.33h2.27l-1.84 1.33.7-2.15zM3.67 13.26l.7 2.15-1.83-1.33h2.27l-1.84 1.33.7-2.15zM3.67 17.42l.7 2.15-1.83-1.33h2.27l-1.84 1.33.7-2.15zM6.12 2.86l.7 2.15-1.83-1.33h2.26L5.42 5.01l.7-2.15zM6.12 7.02l.7 2.15-1.83-1.33h2.26L5.42 9.17l.7-2.15zM6.12 11.18l.7 2.15L4.99 12h2.26l-1.83 1.33.7-2.15zM6.12 15.34l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM8.57.78l.69 2.15L7.44 1.6H9.7L7.87 2.93l.7-2.15zM8.57 4.94l.69 2.15-1.82-1.33H9.7L7.87 7.09l.7-2.15zM8.57 9.1l.69 2.15-1.82-1.33H9.7l-1.83 1.33.7-2.15zM8.57 13.26l.69 2.15-1.82-1.33H9.7l-1.83 1.33.7-2.15zM8.57 17.42l.69 2.15-1.82-1.33H9.7l-1.83 1.33.7-2.15zM11.01 2.86l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM11.01 7.02l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM11.01 11.18l.7 2.15L9.88 12h2.26l-1.83 1.33.7-2.15zM11.01 15.34l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM13.46.78l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM13.46 4.94l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM13.46 9.1l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM13.46 13.26l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM13.46 17.42l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM15.9 2.86l.7 2.15-1.83-1.33h2.26l-1.82 1.33.69-2.15zM15.9 7.02l.7 2.15-1.83-1.33h2.26l-1.82 1.33.69-2.15zM15.9 11.18l.7 2.15L14.77 12h2.26l-1.82 1.33.69-2.15zM15.9 15.34l.7 2.15-1.83-1.33h2.26l-1.82 1.33.69-2.15zM18.35.78l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM18.35 4.94l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM18.35 9.1l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM18.35 13.26l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15zM18.35 17.42l.7 2.15-1.83-1.33h2.26l-1.83 1.33.7-2.15z"
            />
        </Icon>
    )
}

/**
 * GiftIcon component renders a customizable SVG icon representing a gift.
 *
 * This component utilizes the `useIntl` hook to provide internationalization support
 * for the SVG title and description, allowing for default messages or custom messages
 * to be passed as props. The icon is drawn using SVG paths and is styled with strokes.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.svgTitle] - Optional custom title for the SVG element.
 * @param {string} [props.svgDescription] - Optional custom description for the SVG element.
 * @param {Object} props - Additional props are spread onto the root SVG element.
 *
 * @returns {JSX.Element} A JSX element representing the gift icon.
 *
 * @example
 * <GiftIcon svgTitle="Custom Gift Icon" svgDescription="A beautifully wrapped gift" />
 *
 * @component
 */
export const GiftIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 25 24" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="#000"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.427 8.25H4.76a.927.927 0 0 0-.916.938v1.874c0 .518.41.938.916.938h14.667c.506 0 .917-.42.917-.938V9.188a.927.927 0 0 0-.917-.938ZM12.094 8.25v12"
            />
            <path
                stroke="#000"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.844 12v6.417c0 .486-.203.952-.565 1.296a1.98 1.98 0 0 1-1.364.537H7.272a1.98 1.98 0 0 1-1.363-.537 1.789 1.789 0 0 1-.565-1.296V12M7.754 8.25c-.639 0-1.252-.237-1.704-.659a2.176 2.176 0 0 1-.706-1.59c0-.597.254-1.17.706-1.592a2.501 2.501 0 0 1 1.704-.659c.93-.015 1.842.406 2.616 1.21.774.802 1.375 1.949 1.724 3.29.349-1.341.95-2.488 1.723-3.29.774-.804 1.686-1.225 2.616-1.21.64 0 1.253.237 1.705.66.452.421.706.994.706 1.59 0 .597-.254 1.17-.706 1.591a2.501 2.501 0 0 1-1.705.659"
            />
        </Icon>
    )
}

export const HamburgerIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 22 14" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="square"
                strokeWidth="1.3"
                d="M.999 1h20M.999 7h20M.999 13h20"
            />
        </Icon>
    )
}

export const InfoIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 16 16" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.2"
                d="M8 14.667A6.667 6.667 0 1 0 8 1.333a6.667 6.667 0 0 0 0 13.334Z"
                clipRule="evenodd"
            />
            <path
                stroke="currentColor"
                strokeLinecap="square"
                strokeLinejoin="round"
                strokeWidth="1.2"
                d="M8 10.667V8"
            />
            <circle cx="8" cy="5.333" r=".667" fill="currentColor" />
        </Icon>
    )
}

export const LoaderIcon = ({svgTitle, svgDescription, ...props}) => {
    const spinAnimation = `${spin} infinite 1s linear`
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 34 34" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                d="M7.8053 18.5629C6.34444 19.6066 6.39937 20.0671 6.36424 20.6355C6.32528 21.2417 5.81299 22.6873 6.62805 22.8895C7.44248 23.0918 9.14032 21.5134 9.35047 21.2599C9.56127 21.0064 9.49292 20.4312 9.63345 19.2688C9.93175 16.8004 7.8053 18.5629 7.8053 18.5629ZM7.61686 21.388C7.5894 21.4979 7.38244 22.0292 7.36263 21.2005C7.34475 20.4454 7.45653 20.0583 7.45653 20.0583C7.45653 20.0583 7.60473 19.3983 7.66669 20.1669C7.74142 21.0819 7.64433 21.2787 7.6175 21.388H7.61686ZM8.57884 19.8399C8.56096 20.5964 8.43129 20.976 8.43129 20.976C8.43129 20.976 8.25244 21.628 8.22625 20.8573C8.19495 19.939 8.30162 19.7475 8.3342 19.6396C8.36677 19.5318 8.59865 19.0126 8.57948 19.8392L8.57884 19.8399Z"
                fill="#143A64"
            />
            <path
                d="M12.8706 23.4799L11.4149 24.5021C10.7423 24.9181 10.7793 26.1298 10.9671 27.1614C11.1734 28.2975 13.5682 26.3658 13.8626 26.1129C14.1571 25.8607 14.3015 25.7893 14.3436 24.0874C14.3864 22.3843 12.87 23.4799 12.87 23.4799H12.8706ZM12.1341 24.8534C12.7767 24.5196 13.1562 24.465 13.1562 24.465C13.1562 24.465 13.786 24.3302 13.1517 24.7125C12.3948 25.1676 12.1852 25.1568 12.0798 25.1764C11.9744 25.1959 11.4302 25.2195 12.1341 24.8534ZM13.1855 25.6942C12.4126 26.119 12.2038 26.0994 12.0977 26.1149C11.9917 26.1304 11.4475 26.1331 12.1635 25.796C12.8176 25.4872 13.199 25.4474 13.199 25.4474C13.199 25.4474 13.8333 25.3375 13.1855 25.6942Z"
                fill="#143A64"
            />
            <path
                d="M14.7698 20.7533C14.0186 19.7001 13.4833 20.7533 13.4833 20.7533C12.299 22.2967 13.5472 22.1962 14.2549 22.6587C14.9077 23.0855 15.0649 22.8826 15.5816 22.6689C16.0984 22.4558 15.5209 21.8065 14.7698 20.7526V20.7533Z"
                fill="#143A64"
            />
            <path
                d="M11.0196 20.4045C11.0969 20.6479 11.2278 20.8238 11.4463 20.9418C11.6641 21.0605 11.9037 21.1455 12.1317 21.2412C12.3208 21.3208 12.5188 21.3909 12.7053 21.2668C12.9123 21.1293 13.0605 20.8495 13.1039 20.6013C13.2112 19.9932 12.4434 20.0026 12.1387 19.6884C11.9816 19.5259 11.942 19.3048 11.8219 19.1193C11.6973 18.9265 11.4763 18.9366 11.2821 18.9872C11.0656 19.0445 10.7705 19.1652 10.6638 19.3911C10.5405 19.6527 10.8663 20.1745 10.9385 20.2588C10.9602 20.3289 10.9953 20.3269 11.019 20.4045H11.0196Z"
                fill="#143A64"
            />
            <path
                d="M15.1059 19.8943C15.1059 19.8943 15.7287 18.8835 15.0714 18.6186C13.8712 18.1365 14.3081 18.1587 13.9376 17.9733C13.5671 17.7879 13.7063 17.4939 12.8504 17.6726C12.6058 17.7238 12.4869 17.8506 12.4869 17.8506C12.2072 18.1486 12.027 19.3144 12.5099 19.489C13.8782 19.9846 14.4486 20.0669 14.4486 20.0669C15.0676 20.1336 15.1059 19.8943 15.1059 19.8943Z"
                fill="#143A64"
            />
            <path
                d="M11.1634 18.712C11.6131 18.7693 12.0513 18.0222 12.0315 17.5934C12.0117 17.1652 11.9951 17.2279 11.2043 17.1875C10.4122 17.147 10.4442 17.9575 10.4442 17.9575C9.91845 18.836 10.7137 18.6546 11.1634 18.7126V18.712Z"
                fill="#143A64"
            />
            <path
                d="M15.9009 21.6335C16.7243 22.8181 17.6837 21.4144 18.3091 20.7637C18.9344 20.1124 17.8044 19.8224 16.7722 19.1367C15.7406 18.4517 15.6262 19.4219 15.6262 19.4219C14.9345 21.2782 15.1804 20.5985 15.9009 21.6335Z"
                fill="#143A64"
            />
            <path
                d="M19.5428 18.7666C18.8146 17.919 19.2681 17.5779 17.7345 17.0425C17.3148 16.8962 16.8351 16.8598 16.6613 17.3621C16.2289 18.6149 17.6674 19.2473 18.508 19.7044C19.0235 20.1805 19.9184 19.2042 19.5422 18.7666H19.5428Z"
                fill="#143A64"
            />
            <path
                d="M11.5537 23.3354C11.5933 23.3226 11.7523 23.2822 12.7156 22.9626C13.5735 22.6787 13.0126 22.2782 12.4863 22.4036C11.8718 22.5493 11.9044 22.138 11.9044 22.138C12.036 21.5507 11.6291 21.1933 11.332 21.3673C10.9954 21.5662 10.8849 21.3012 10.7929 21.2001C10.7418 21.1441 10.5911 20.2453 10.5911 20.2453C10.5834 20.2238 10.5636 20.1901 10.554 20.1766C10.2538 19.7558 10.0807 20.2406 10.0807 20.2406L9.95932 20.7335L9.83348 21.4981L9.69232 22.082C9.31034 23.2835 9.80602 22.8466 9.80602 22.8466L9.80027 22.8783C8.6128 24.9968 10.4614 23.4157 10.4614 23.4157C10.326 24.2774 10.8306 23.8216 11.0446 23.6261C11.2369 23.4501 11.3397 23.4069 11.5537 23.3348V23.3354Z"
                fill="#143A64"
            />
            <path
                d="M15.8227 16.3636C15.7039 16.2639 15.5927 16.1546 15.4841 16.042C15.4484 16.0056 15.4036 15.9395 15.2344 15.7959C15.129 15.7069 14.9546 15.6563 14.8288 15.624C14.7304 15.5977 14.6301 15.5754 14.5285 15.5606C14.3203 15.5323 14.0967 15.5471 13.9447 15.7184C13.8636 15.8101 13.8291 15.9166 13.8182 16.0386L13.6547 16.6165C13.518 17.6366 15.2325 18.2954 15.7492 18.3749C15.8866 18.3958 15.9907 18.3095 16.059 18.2097C16.1421 18.0897 16.1555 18.0317 16.1829 17.8881C16.2168 17.7047 16.2423 17.5173 16.2347 17.3305C16.2283 17.1599 16.213 16.9786 16.1829 16.81C16.1446 16.599 15.9728 16.487 15.8233 16.3623L15.8227 16.3636Z"
                fill="#143A64"
            />
            <path
                d="M11.3283 15.2511C10.3375 16.6974 11.0364 17.05 11.3161 16.8673C11.6738 16.6327 12.0169 16.8444 12.0169 16.8444C12.7987 17.3271 12.9731 17.0069 12.9501 16.7176C12.9271 16.427 13.5033 15.8263 13.5033 15.8263C14.0762 15.0751 12.8703 15.51 12.2513 15.0718C11.6323 14.6335 11.3289 15.2504 11.3289 15.2504L11.3283 15.2511Z"
                fill="#143A64"
            />
            <path
                d="M17.3627 16.2397C18.3936 15.8392 17.8283 15.4912 17.9171 15.2094C18.0212 14.8831 17.7395 14.3045 17.3275 14.3362C16.9551 14.3652 16.9724 13.8858 16.9724 13.8858C16.9698 13.7314 17.3761 13.4031 16.4339 13.2743C15.8775 13.1994 15.6131 13.5123 15.4917 13.7807C15.4068 13.9674 15.3058 14.1421 15.3058 14.1421C15.2503 14.2385 15.3142 14.1501 15.1781 14.3322C14.5304 15.1979 16.149 16.0542 16.3157 16.1621C16.591 16.3415 16.7341 16.4851 17.3627 16.2397Z"
                fill="#143A64"
            />
            <path
                d="M20.5971 17.5326C20.9044 16.5488 20.6361 16.1861 20.3749 15.6237C20.113 15.0621 20.3174 14.9178 19.2385 14.932C18.1596 14.9454 18.2586 15.9083 18.2586 15.9083C18.1047 16.9561 18.8801 17.1455 19.4052 18.0045C19.7655 18.5938 20.2618 18.6087 20.5971 17.5326Z"
                fill="#143A64"
            />
            <path
                d="M18.2566 14.5616C18.9733 14.8515 19.5137 14.0842 19.8191 13.4491C20.3 12.4505 19.3508 12.7701 18.4483 12.2529C17.5591 11.7425 17.3828 12.2489 17.3828 12.2489C17.3828 12.2489 16.8846 12.7242 17.4959 13.6459C17.5489 13.7255 17.7296 14.0343 17.8127 14.1078C18.042 14.3114 18.0707 14.4867 18.256 14.5616H18.2566Z"
                fill="#143A64"
            />
            <path
                d="M14.6223 11.7763C13.2969 12.7014 14.3029 13.0236 14.3029 13.0236C14.4409 13.1059 15.6194 13.4545 15.6437 13.0156C15.6686 12.5773 16.1675 12.6043 16.1675 12.6043C17.0598 12.9171 17.1882 11.7176 17.1882 11.7176C17.3786 10.8236 15.9484 10.8519 14.6223 11.7763Z"
                fill="#143A64"
            />
            <path
                d="M13.862 13.2689C13.3229 12.7201 12.7294 13.246 12.7294 13.246C11.6154 13.9958 12.0849 14.5601 12.9901 14.8312C13.952 15.1191 13.9718 15.1245 14.5716 14.4893C15.1721 13.8535 14.6981 13.2999 14.4094 13.4422C14.1213 13.5851 13.8626 13.2689 13.8626 13.2689H13.862Z"
                fill="#143A64"
            />
            <path
                d="M18.265 10.9905C16.9945 11.3519 18.0401 11.6405 18.0401 11.6405C18.624 11.6688 19.3426 12.0005 19.3426 12.0005C20.2599 12.8096 20.2643 12.3667 20.2707 12.2568C20.2771 12.1475 19.5355 10.6291 18.265 10.9905Z"
                fill="#143A64"
            />
            <path
                d="M20.8161 13.7881C20.5612 12.8441 20.1684 13.6984 20.1684 13.6984C19.994 13.9256 20.0061 14.3423 20.5018 15.1865C21.1016 16.2073 21.0971 14.8277 20.8161 13.7881Z"
                fill="#143A64"
            />
            <path
                d="M9.55873 17.0341C9.62452 18.299 10.0225 17.1177 10.0225 17.1177C10.0225 17.1177 10.6996 15.3235 11.017 14.7214C11.3351 14.1193 12.2486 12.9029 13.406 11.9758C13.65 11.781 13.9094 11.6043 14.1802 11.4526C14.5833 11.2281 15.0112 11.0602 15.4482 10.924C15.8723 10.7912 16.3003 10.6442 16.7308 10.5356C17.0483 10.4547 17.374 10.4237 17.7004 10.4116C18.0339 10.3988 18.3724 10.3913 18.6995 10.4743C18.8323 10.5087 18.9646 10.5478 19.0949 10.5909C19.1875 10.6213 19.5082 10.7386 19.5739 10.6024C19.6538 10.4365 19.2929 10.2491 19.2035 10.1958C18.9786 10.0623 18.7327 9.94229 18.4855 9.86206C18.3405 9.81486 18.1897 9.79261 18.0396 9.78182C17.4826 9.74136 16.9275 9.83374 16.3731 9.87959C16.2504 9.88903 16.1252 9.92678 16.0045 9.94903C15.5369 10.0353 15.0885 10.1911 14.6459 10.3509C14.4466 10.423 14.2543 10.5141 14.0652 10.6118C13.4137 10.951 12.7328 11.2928 12.1617 11.7709C11.5363 12.2954 10.9397 12.8665 10.3636 13.4504C10.2096 13.6055 10.071 13.7768 9.95285 13.9642C9.79188 14.2204 9.65965 14.5077 9.59897 14.8097C9.54404 15.0815 9.54403 15.3431 9.55298 15.6195C9.55298 15.6472 9.55426 15.6748 9.55426 15.7024C9.55298 15.8966 9.5319 16.0881 9.53381 16.2823C9.53573 16.5331 9.54659 16.7839 9.55937 17.0341H9.55873Z"
                fill="#143A64"
            />
            <path
                d="M21.7669 17.7892C21.8461 17.5511 21.924 17.3131 21.9866 17.069C22.0115 16.9747 22.0365 16.8809 22.0575 16.7865C22.124 16.4953 22.1399 16.1568 22.1463 15.8581C22.1502 15.641 22.1355 15.2776 22.0122 15.0975C21.9598 15.0207 21.7222 14.722 21.6149 14.817C21.5421 14.8811 21.5216 15.0382 21.5037 15.1292C21.4954 15.1717 21.4884 15.2149 21.482 15.2573C21.3977 15.8102 21.4418 16.3732 21.2897 16.9187C21.2227 17.1581 21.1377 17.39 21.0528 17.6226C20.9474 17.9112 20.865 18.2065 20.7295 18.4816C20.4312 19.0898 20.0531 19.764 19.1064 20.8604C18.8669 21.1375 18.6152 21.4005 18.3399 21.6378C17.995 21.9345 17.6085 22.1516 17.2157 22.3687C16.6293 22.6923 16.0078 22.9061 15.3824 23.1265C15.2802 23.1623 14.7398 23.3578 15.0688 23.467C15.2655 23.5338 15.4751 23.5297 15.6775 23.5446C15.8883 23.5594 16.0979 23.5783 16.3086 23.5945C16.6747 23.6228 16.996 23.6073 17.3294 23.4239C17.6705 23.2351 17.9624 22.9708 18.2805 22.7469C18.4766 22.6094 18.667 22.4638 18.8509 22.3087C19.1071 22.0936 19.3524 21.8603 19.5727 21.6041C19.8027 21.3391 19.9982 21.0444 20.2051 20.7592C20.7819 19.969 20.9678 19.555 20.8145 19.8369C20.6638 20.1153 21.1103 19.2745 21.2374 18.9819C21.3594 18.6987 21.6685 18.0798 21.7656 17.7871L21.7669 17.7892Z"
                fill="#143A64"
            />
            <path
                d="M24.7134 17.9799C24.7134 17.9799 23.982 16.9321 23.3631 16.0266C23.2583 15.8728 23.1235 15.6793 22.9485 15.6112C22.7492 15.5344 22.601 15.6429 22.5071 15.8263C22.4995 15.8412 22.4924 15.856 22.4854 15.8715C22.2938 16.2848 22.3289 16.7366 22.3813 17.1802C22.518 18.3312 22.106 18.8854 22.106 18.8854C22.106 18.8854 21.303 19.9986 21.7668 20.5946C22.2305 21.19 21.8422 22.4185 21.8422 22.4185C21.7962 22.5621 21.7904 22.7529 21.7943 22.904C21.7987 23.0691 21.8185 23.2424 21.9367 23.3631C22.0421 23.471 22.2063 23.529 22.3487 23.5458C22.6004 23.5762 22.8291 23.5 23.0418 23.3604C23.2468 23.2276 23.4167 23.0429 23.5873 22.8669C23.7738 22.6754 23.9156 22.4461 24.0785 22.2297C24.4924 21.6802 24.7888 21.2278 24.8993 20.6351C25.0092 20.0438 25.0175 20.1564 25.0775 19.6601C25.1171 19.3318 25.0124 18.5867 24.8955 18.2732C24.852 18.1545 24.7811 18.0776 24.7128 17.9792L24.7134 17.9799ZM22.5927 21.1233C22.5927 21.1233 22.571 20.6054 22.4452 20.3593C22.4452 20.3593 22.6445 19.143 23.0597 19.9703C23.4742 20.7976 22.5927 21.1233 22.5927 21.1233ZM24.1775 20.3364C23.9316 20.6452 23.6761 20.0053 23.6013 19.8314C23.1421 18.764 23.0769 18.3736 23.0769 18.3736C23.0769 18.3736 22.8808 17.7183 23.2481 18.1087C23.5125 18.3905 23.6888 18.789 23.8613 19.1356C23.9999 19.4133 24.4368 20.0101 24.1775 20.3364Z"
                fill="#143A64"
            />
            <path
                d="M10.6529 9.24235C10.8611 9.38057 11.2648 9.39945 11.4973 9.33404C11.6717 9.28348 11.8378 9.20594 12.0141 9.15874C12.5404 9.02052 13.153 9.1021 13.4839 9.60442C13.6404 9.84041 13.8857 10.1216 14.1827 10.1128C14.4503 10.1054 14.6675 9.92064 14.893 9.79254C15.1293 9.65836 15.3848 9.54913 15.6391 9.47631C16.1392 9.33404 16.6656 9.27471 17.1823 9.29763C17.3912 9.30707 17.5956 9.33404 17.8013 9.37113C18.0313 9.41361 18.3494 9.48103 18.578 9.39945C19.0597 9.22819 18.2133 8.40358 18.0728 8.26872C17.8058 8.01251 17.5266 7.76708 17.2283 7.55199C16.6969 7.16834 16.113 6.89932 15.4883 6.73615C14.9645 6.5986 14.4197 6.49948 13.8774 6.48465C13.2501 6.46712 12.3066 6.69637 11.0259 7.61133C10.8643 7.72662 10.6995 7.85541 10.5673 8.00779C10.4325 8.16421 10.299 8.35705 10.2888 8.57416C10.2811 8.75082 10.3744 8.95511 10.5008 9.10345C10.5475 9.15941 10.5998 9.20661 10.6529 9.24235ZM14.9543 7.31466C15.8658 7.77854 16.136 8.01453 16.136 8.01453C16.136 8.01453 16.6439 8.35435 16.1884 8.30985C15.8607 8.27884 15.5171 8.10825 15.2079 7.97138C14.9594 7.86215 13.9821 7.71247 13.947 7.31668C13.9138 6.94179 14.8055 7.23779 14.9543 7.31398V7.31466ZM14.6522 8.53101C15.2909 9.18032 14.1284 9.04681 14.1284 9.04681C13.9387 8.85128 13.473 8.68541 13.473 8.68541C13.473 8.68541 14.0141 7.8817 14.6522 8.53101Z"
                fill="#143A64"
            />
            <path
                d="M25.7686 9.68249C25.6293 9.56382 25.4767 9.45999 25.3214 9.36357C25.2473 9.31772 25.1605 9.28198 25.0896 9.23007C25.0372 9.19163 24.9695 9.17006 24.9094 9.14848C24.7184 9.07903 24.5236 9.02442 24.3282 8.96913C24.0982 8.90373 23.7961 8.86057 23.5578 8.83832C23.1714 8.80191 22.8149 8.89361 22.4444 8.99071C22.2854 9.03251 22.127 9.08645 21.9775 9.15859C21.7846 9.25232 21.6013 9.36896 21.4473 9.52674C21.3943 9.58135 21.3458 9.64338 21.3023 9.70541C21.2436 9.78902 21.1784 9.86791 21.1286 9.95691C21.1158 9.97984 21.0915 10.0398 21.0749 10.0601C21.0194 10.1302 20.9919 10.168 20.9325 10.234C20.8386 10.3406 20.7479 10.4498 20.6629 10.5644C20.5039 10.7775 20.3506 11.0654 20.311 11.3385C20.2797 11.5448 20.3461 11.7477 20.4598 11.9149C20.576 12.0842 20.7587 12.1846 20.8737 12.3546C21.0219 12.5723 21.1024 12.8609 21.1733 13.1144C21.2174 13.2695 21.2576 13.4259 21.2902 13.5844C21.2889 13.7435 21.3068 13.9215 21.363 14.0699C21.4231 14.227 21.5502 14.3766 21.7137 14.4131C21.9583 14.4683 22.0439 14.2168 22.1442 14.0665C22.2266 13.9424 22.2867 13.8393 22.4112 13.7651C22.5268 13.6963 22.6425 13.7024 22.7696 13.7004C22.8635 13.6983 22.9618 13.6977 23.0551 13.6842C23.4083 13.635 23.7744 13.4057 24.0765 13.2109C24.3147 13.0571 24.5824 12.8656 24.7248 12.6034C24.79 12.4847 24.8788 12.3802 24.9708 12.2838C25.1509 12.0963 25.3521 11.9325 25.5597 11.7794C25.7756 11.621 25.8158 11.5171 25.9723 11.2926C26.0592 11.1672 26.1263 10.9919 26.1467 10.8368C26.2049 10.3985 26.0771 9.94882 25.7686 9.68451V9.68249ZM22.1027 10.648C22.5441 10.058 23.5431 10.1113 23.5431 10.1113C22.6469 10.1531 22.408 10.7775 22.408 10.7775C21.6939 11.3594 22.1021 10.648 22.1021 10.648H22.1027ZM22.3454 11.116C22.8769 10.4168 23.4103 10.4754 23.4103 10.4754C23.1183 10.4613 22.7792 11.236 22.7792 11.236C21.966 11.8792 22.3454 11.116 22.3454 11.116ZM23.8286 12.1051C23.8286 12.1051 23.4179 12.1469 23.4256 12.3721C23.4326 12.5966 22.8501 12.687 23.1113 12.1071C23.2991 11.6911 23.8619 11.8023 23.8619 11.8023C23.8619 11.8023 24.5875 11.9358 23.8286 12.1051Z"
                fill="#143A64"
            />

            <chakra.g animation={spinAnimation} transformOrigin="center">
                <circle
                    cx={17}
                    cy={17}
                    r={16}
                    fill="transparent"
                    stroke="#143A64"
                    strokeWidth={0.7}
                    strokeOpacity={0.15}
                />
                <path
                    fill="none"
                    stroke="#143A64"
                    strokeWidth={0.7}
                    d="M17,33 a16,16 0 0,1 -16,-16"
                />
            </chakra.g>
        </Icon>
    )
}

export const LocationIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 32" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <circle cx="16" cy="16" r="15.75" stroke="#D9D9D9" strokeWidth=".5" />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.5 14.727c0 3.898-6 8.273-6 8.273s-6-4.284-6-8.273c0-1.519.632-2.975 1.757-4.05A6.148 6.148 0 0 1 15.5 9c1.591 0 3.117.603 4.243 1.678a5.598 5.598 0 0 1 1.757 4.05Z"
            />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.5 16.636c1.105 0 2-.854 2-1.909 0-1.054-.895-1.909-2-1.909s-2 .855-2 1.91c0 1.054.895 1.908 2 1.908Z"
            />
        </Icon>
    )
}

/**
 * PinIcon component renders a customizable SVG icon with a pin shape.
 *
 * This component utilizes the `useIntl` hook to provide internationalization support
 * for the title and description of the SVG, allowing for dynamic localization based on
 * the user's language preferences. If no custom title or description is provided via
 * props, default messages are used.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} [props.svgTitle] - Optional custom title for the SVG element. If not provided, a default title is used.
 * @param {string} [props.svgDescription] - Optional custom description for the SVG element. If not provided, a default description is used.
 * @returns {JSX.Element} A React component that renders an SVG icon with a pin shape.
 *
 * @example
 * <PinIcon svgTitle="Custom Pin Title" svgDescription="Custom Pin Description" />
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Element/title} for more information on the <title> element in SVG.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Element/desc} for more information on the <desc> element in SVG.
 */
export const PinIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 34" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                d="M28.0938 14.3335C28.0938 22.5002 16.0938 31.6668 16.0938 31.6668C16.0938 31.6668 4.09375 22.6911 4.09375 14.3335C4.09375 11.1509 5.35803 8.09865 7.60847 5.84821C9.85891 3.59778 12.9112 2.3335 16.0938 2.3335C19.2763 2.3335 22.3286 3.59778 24.579 5.84821C26.8295 8.09865 28.0938 11.1509 28.0938 14.3335Z"
                stroke="black"
                strokeWidth="2.93333"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M16.0938 18.3335C18.3029 18.3335 20.0938 16.5426 20.0938 14.3335C20.0938 12.1244 18.3029 10.3335 16.0938 10.3335C13.8846 10.3335 12.0938 12.1244 12.0938 14.3335C12.0938 16.5426 13.8846 18.3335 16.0938 18.3335Z"
                stroke="black"
                strokeWidth="2.93333"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </Icon>
    )
}

export const MinusIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" d="M1.498 6h9" />
        </Icon>
    )
}

export const NoLocationIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 13 14" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.1"
                d="M2.817 2.818A4.5 4.5 0 0 1 10.498 6c0 1.073-.551 2.191-1.269 3.192m-1.516 1.772c-.92.93-1.714 1.536-1.714 1.536s-4.5-3.366-4.5-6.5c0-.376.047-.747.138-1.106"
            />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.1"
                d="M7.499 6a1.5 1.5 0 0 0-2.746-.836l2.081 2.082c.401-.27.665-.727.665-1.246Z"
            />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.1"
                d="M11.498 12 .998 1.5"
            />
        </Icon>
    )
}

/**
 * A React functional component that renders a custom SVG icon representing orders.
 *
 * This component utilizes the `useIntl` hook from `react-intl` to provide internationalization
 * support for the icon's title and description. If `svgTitle` and `svgDescription` props are not
 * provided, default messages are used.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.svgTitle] - Optional title for the SVG, used for accessibility purposes.
 * @param {string} [props.svgDescription] - Optional description for the SVG, used for accessibility purposes.
 * @param {...Object} props - Additional properties are spread onto the root SVG element.
 *
 * @returns {JSX.Element} A JSX element representing the orders icon.
 *
 * @example
 * <OrdersIcon svgTitle="My Orders Icon" svgDescription="An icon representing orders" />
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG|MDN SVG Documentation}
 * @see {@link https://formatjs.io/docs/react-intl|React Intl Documentation}
 */
export const OrdersIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 33 34" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.0937 2.33301L29.427 8.99963V24.9996L16.0937 31.6663L2.76041 24.9996V8.99963L16.0937 2.33301Z"
                stroke="black"
                strokeWidth="2.93333"
                strokeLinecap="round"
            />
            <path
                d="M3.18686 9.21289L16.0935 15.6662L29.0002 9.21289"
                stroke="black"
                strokeWidth="2.93333"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M16.0938 31.347V15.667"
                stroke="black"
                strokeWidth="2.93333"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </Icon>
    )
}

export const PhoneIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 32" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <circle cx="16" cy="16" r="15.75" stroke="#D9D9D9" strokeWidth=".5" />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m20.079 17.801-2.746.532c-1.854-.93-3-2-3.666-3.666l.513-2.754-.97-2.58h-2.501c-.752 0-1.344.622-1.231 1.365.28 1.855 1.106 5.22 3.522 7.635 2.537 2.537 6.19 3.638 8.201 4.076.777.169 1.466-.437 1.466-1.232v-2.39l-2.588-.986Z"
            />
        </Icon>
    )
}

export const PlusIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="currentColor" d="M1.499 6h9M5.999 1.5v9" />
        </Icon>
    )
}

export const PrivacyIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.1"
                d="M5.999 11s4-2 4-5V2.5l-4-1.5-4 1.5V6c0 3 4 5 4 5Z"
            />
            <path
                stroke="currentColor"
                strokeLinecap="square"
                strokeLinejoin="round"
                strokeWidth="1.1"
                d="M6.049 7.95v-2.2"
            />
            <circle cx="6.049" cy="3.55" r=".55" fill="currentColor" />
        </Icon>
    )
}

export const RefreshIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 15 15" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <g
                clipPath="url(#a)"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M13.228 5A6.252 6.252 0 0 0 1.28 6.875" />
                <path d="M10.623 5h2.75a.375.375 0 0 0 .376-.375v-2.75M1.8 10a6.251 6.251 0 0 0 11.949-1.875" />
                <path d="M4.404 10h-2.75a.375.375 0 0 0-.375.375v2.75" />
            </g>
            <defs>
                <clipPath id="a">
                    <path fill="#fff" d="M-.001 0h15v15h-15z" />
                </clipPath>
            </defs>
        </Icon>
    )
}

export const RepairIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 14" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeWidth="1.1"
                d="M1.449 1.45h9.1v2.1h-9.1zM1.449 10.45h9.1v2.1h-9.1z"
            />
            <path fill="#D9D9D9" stroke="currentColor" d="M3.499 3.5h5v7h-5z" />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                d="m3.999 8 4 1M3.999 6l4 1M3.999 4l4 1M3.999 10l2 .5"
            />
        </Icon>
    )
}

export const RightArrowIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 17 16" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                d="M8.97998 13L8.30562 12.3186L12.1474 8.47685H3.97998V7.52315H12.1474L8.30562 3.68142L8.97998 3L13.98 8L8.97998 13Z"
                fill="currentColor"
            />
        </Icon>
    )
}

export const SearchIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                d="M5.132 10.263A5.1 5.1 0 0 0 8.273 9.18l2.82 2.82.907-.907-2.82-2.82a5.1 5.1 0 0 0 1.084-3.142A5.138 5.138 0 0 0 5.132 0 5.138 5.138 0 0 0 0 5.131a5.138 5.138 0 0 0 5.132 5.132Zm0-8.98A3.853 3.853 0 0 1 8.98 5.13a3.853 3.853 0 0 1-3.85 3.85 3.853 3.853 0 0 1-3.848-3.85 3.853 3.853 0 0 1 3.849-3.848Z"
            />
        </Icon>
    )
}

/**
 * SecurePaymentIcon is a React functional component that renders a custom SVG icon
 * representing secure payment. This component utilizes the `useIntl` hook from
 * `react-intl` to provide internationalization support for the title and description
 * of the SVG, allowing for localization of these attributes.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.svgTitle] - An optional title for the SVG element. If not provided,
 *                                    a default title will be used.
 * @param {string} [props.svgDescription] - An optional description for the SVG element. If not
 *                                          provided, a default description will be used.
 * @param {Object} [props...] - Additional properties that can be spread onto the SVG element.
 *
 * @returns {JSX.Element} A JSX element representing the secure payment icon.
 *
 * The SVG icon is constructed with a viewBox of "0 0 34 34" and uses the `currentColor`
 * for its fill, allowing it to inherit the color from its parent element. The icon
 * includes a title and description for accessibility purposes.
 */
export const SecurePaymentIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 34 34" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                d="M12.517 9.87c.029.038.051.077.077.119a2.157 2.157 0 0 1 3.994 1.126v.135l.54.3.387-1.636a.321.321 0 0 1 .608-.054c.438 1.007 1.458 1.458 1.986 1.628a4.757 4.757 0 0 0-.19-1.429.322.322 0 0 1 .602-.225c.26.599 1.12 1.162 1.7 1.477.357-3.73-3.348-4.506-3.512-4.55-3.502-.406-5.671 2.355-6.192 3.108ZM3.588 9.821a.322.322 0 0 1 .622.164 6.736 6.736 0 0 0-.084 1.436 2.781 2.781 0 0 0 1.93-1.545.322.322 0 0 1 .323-.19.322.322 0 0 1 .273.27 8.64 8.64 0 0 0 .393 1.57 5.972 5.972 0 0 0 1.706-1.248 2.17 2.17 0 0 1 1.995-1.33h.107a5.352 5.352 0 0 0-.239-.282c-.927-1.034-2.816-2.43-5.909-1.706-.103.035-3.112 1.09-2.8 4.364.531-.245 1.43-.772 1.683-1.503Z"
            />
            <path
                fill="currentColor"
                d="M14.428 12.621a1.516 1.516 0 1 0-1.51-1.515c.004.143.027.284.069.421a2.173 2.173 0 0 1 1.441 1.095ZM25.767 21.134a24.98 24.98 0 0 0-.553-7.331 8.368 8.368 0 0 0-2.575 3.015c.119.576.82 3.08 3.128 4.316ZM21.303 20.436a27.905 27.905 0 0 0-.643 7.335l3.05.38a14.752 14.752 0 0 0 1.398-3.3c-2.025-1.039-3.258-3.183-3.805-4.415ZM25.712 21.836c-2.153-1.043-3.109-3.003-3.495-4.14a15.396 15.396 0 0 0-.644 1.716c.222.599 1.404 3.54 3.702 4.8.197-.782.343-1.576.437-2.376ZM12.275 11.105a1.516 1.516 0 1 0-1.65 1.503 2.163 2.163 0 0 1 1.686-1.13 2.228 2.228 0 0 1-.036-.373ZM12.066 9.39a8.922 8.922 0 0 1 2.5-2.317v-1.61l-.238-.389a.32.32 0 0 1-.022-.293l.251-.595a.586.586 0 0 0-.245-.419 1.004 1.004 0 0 0-.733-.013C11.75 5.01 11.299 7.79 11.229 8.4c.23.263.436.545.618.843.07.045.148.097.219.148ZM12.517 15.151a1.516 1.516 0 1 0 0-3.032 1.516 1.516 0 0 0 0 3.032ZM24.625 8.044h5.593a1.61 1.61 0 0 0 0-3.218.322.322 0 0 1-.225-.106.322.322 0 0 1-.096-.232.166.166 0 0 0 0-.039c0-.914-1.05-1.66-2.35-1.66s-2.356.746-2.356 1.667v.032a.321.321 0 0 1-.322.328h-.247a1.609 1.609 0 1 0 0 3.219l.003.01ZM17.064 24.89h.531c-.215-1.493-1.155-7.402-3.012-10.621a2.157 2.157 0 0 1-1.867 1.503 33.414 33.414 0 0 1 1.773 9.26 21.654 21.654 0 0 1 2.575-.142Z"
            />
            <path
                fill="currentColor"
                d="M31.796 30.574h-3.367c-.512-1.287-1.738-2.449-3.518-3.35-.164.347-.325.67-.512 1.01l1.137.142a.322.322 0 1 1-.042.644h-.039l-1.57-.197a.345.345 0 0 1-.084 0l-5.027-.64a.322.322 0 0 1-.273-.236.324.324 0 0 1 .353-.408l1.155.145a27.963 27.963 0 0 1-.022-1.947 20.68 20.68 0 0 0-2.923-.21c-5.445 0-10.202 2.141-11.364 5.04H2.748a.322.322 0 0 0 0 .645h29.048a.322.322 0 1 0 0-.644v.006Z"
            />
        </Icon>
    )
}

export const SecurePaymentsIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 32" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="#000"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.4"
                d="M26.056 17.255c0 6.285-4.4 9.428-9.629 11.25a1.257 1.257 0 0 1-.842-.012c-5.242-1.81-9.641-4.953-9.641-11.238V8.456A1.257 1.257 0 0 1 7.2 7.2c2.514 0 5.656-1.508 7.843-3.419a1.47 1.47 0 0 1 1.911 0c2.2 1.923 5.33 3.42 7.844 3.42a1.257 1.257 0 0 1 1.257 1.256v8.8Z"
            />
            <path
                stroke="#000"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.4"
                d="m12.229 15.998 2.514 2.514 5.028-5.028"
            />
        </Icon>
    )
}

export const ShopIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 32" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <circle cx="16" cy="16" r="15.75" stroke="#D9D9D9" strokeWidth=".5" />
            <path
                stroke="currentColor"
                d="M9.5 14.667v6c0 .736.597 1.333 1.333 1.333h9.334c.736 0 1.333-.597 1.333-1.333v-6"
            />
            <path
                stroke="currentColor"
                strokeMiterlimit="16"
                d="M17.389 22v-4c0-.736-.597-1.333-1.333-1.333h-1.334c-.736 0-1.333.597-1.333 1.333v4"
            />
            <path
                stroke="currentColor"
                d="m22.046 14.243-1.13-3.953a.4.4 0 0 0-.384-.29h-2.699l.317 3.803c.01.124.078.236.185.3.26.155.768.442 1.165.564.677.208 1.667.133 2.23.063a.38.38 0 0 0 .316-.487Z"
            />
            <path
                stroke="currentColor"
                d="M16.833 14.667c.379-.117.859-.383 1.128-.542a.386.386 0 0 0 .186-.366L17.833 10h-4.666l-.314 3.76a.386.386 0 0 0 .186.365c.27.159.75.425 1.128.542.995.306 1.671.306 2.666 0Z"
            />
            <path
                stroke="currentColor"
                d="m10.084 10.29-1.13 3.953a.38.38 0 0 0 .315.487c.564.07 1.554.145 2.231-.063.397-.122.905-.409 1.165-.564a.385.385 0 0 0 .185-.3L13.167 10h-2.699a.4.4 0 0 0-.384.29Z"
            />
        </Icon>
    )
}

export const StoreVBQIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const generatedId = useId()
    const iconId = `storeVBQIcon-${generatedId}`
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Vilebrequin store icon',
            id: 'stores.vbq.icon.svg.title'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'turtle logo',
            id: 'stores.vbq.icon.svg.description'
        })

    return (
        <Icon viewBox="0 0 24 25" fill="none" role="img" aria-labelledby={iconId} {...props}>
            <title id={iconId}>{title}</title>
            <desc>{description}</desc>
            <rect x="0.5" y="1.03906" width="23" height="23" rx="11.5" stroke="#112A46" />
            <g clipPath="url(#clip0_1248_24845)">
                <path
                    d="M5.3091 13.7294C4.13987 14.5234 4.18384 14.8737 4.15572 15.3061C4.12454 15.7672 3.71452 16.8669 4.36687 17.0208C5.01871 17.1747 6.37761 15.974 6.54581 15.7811C6.71452 15.5882 6.65982 15.1507 6.77229 14.2664C7.01105 12.3886 5.3091 13.7294 5.3091 13.7294ZM5.15828 15.8786C5.1363 15.9622 4.97065 16.3663 4.95481 15.736C4.94049 15.1615 5.02996 14.8671 5.02996 14.8671C5.02996 14.8671 5.14857 14.3649 5.19816 14.9497C5.25798 15.6457 5.18027 15.7955 5.15879 15.8786H5.15828ZM5.92822 14.7009C5.91391 15.2764 5.81012 15.5652 5.81012 15.5652C5.81012 15.5652 5.66698 16.0612 5.64601 15.4749C5.62096 14.7763 5.70634 14.6306 5.73241 14.5485C5.75849 14.4665 5.94407 14.0715 5.92873 14.7004L5.92822 14.7009Z"
                    fill="#112A46"
                />
                <path
                    d="M9.36327 17.467L8.19813 18.2446C7.65979 18.5611 7.68944 19.4828 7.83975 20.2676C8.00488 21.1318 9.92155 19.6623 10.1572 19.47C10.3929 19.2781 10.5085 19.2238 10.5422 17.9292C10.5765 16.6335 9.36276 17.467 9.36276 17.467H9.36327ZM8.7738 18.5118C9.28811 18.2579 9.5918 18.2164 9.5918 18.2164C9.5918 18.2164 10.0959 18.1138 9.58822 18.4046C8.98239 18.7509 8.8147 18.7426 8.73034 18.7575C8.64599 18.7724 8.2104 18.7903 8.7738 18.5118ZM9.61531 19.1514C8.9967 19.4746 8.82952 19.4597 8.74466 19.4715C8.65979 19.4833 8.22421 19.4854 8.79732 19.2289C9.32083 18.994 9.62605 18.9637 9.62605 18.9637C9.62605 18.9637 10.1337 18.8801 9.61531 19.1514Z"
                    fill="#112A46"
                />
                <path
                    d="M10.8833 15.3951C10.282 14.594 9.8536 15.3951 9.8536 15.3951C8.90575 16.5692 9.90473 16.4928 10.4712 16.8447C10.9937 17.1693 11.1195 17.0149 11.5331 16.8524C11.9467 16.6903 11.4845 16.1963 10.8833 15.3946V15.3951Z"
                    fill="#112A46"
                />
                <path
                    d="M7.88179 15.1293C7.94365 15.3145 8.04846 15.4484 8.2233 15.5381C8.39764 15.6284 8.58936 15.693 8.77187 15.7659C8.9232 15.8264 9.08169 15.8797 9.23098 15.7853C9.39662 15.6807 9.51523 15.4678 9.54999 15.2791C9.63588 14.8164 9.02136 14.8236 8.7775 14.5846C8.65173 14.461 8.62003 14.2927 8.52392 14.1517C8.42423 14.005 8.24733 14.0127 8.09191 14.0512C7.9186 14.0948 7.6824 14.1866 7.59702 14.3584C7.49835 14.5574 7.75909 14.9544 7.81686 15.0185C7.83424 15.0719 7.86236 15.0703 7.88128 15.1293H7.88179Z"
                    fill="#112A46"
                />
                <path
                    d="M11.1522 14.7401C11.1522 14.7401 11.6507 13.9712 11.1246 13.7697C10.1639 13.4029 10.5136 13.4198 10.2171 13.2788C9.92059 13.1377 10.032 12.9141 9.34696 13.05C9.15116 13.089 9.05606 13.1854 9.05606 13.1854C8.83214 13.4121 8.68797 14.299 9.07447 14.4318C10.1696 14.8088 10.6261 14.8714 10.6261 14.8714C11.1215 14.9222 11.1522 14.7401 11.1522 14.7401Z"
                    fill="#112A46"
                />
                <path
                    d="M7.99668 13.8405C8.3566 13.8841 8.70732 13.3158 8.69147 12.9896C8.67562 12.6639 8.66233 12.7116 8.0294 12.6808C7.39545 12.65 7.42102 13.2666 7.42102 13.2666C7.00026 13.9349 7.63676 13.7969 7.99668 13.841V13.8405Z"
                    fill="#112A46"
                />
                <path
                    d="M11.7887 16.0621C12.4477 16.9633 13.2155 15.8954 13.7161 15.4005C14.2166 14.905 13.3122 14.6844 12.486 14.1628C11.6603 13.6417 11.5688 14.3797 11.5688 14.3797C11.0151 15.7918 11.212 15.2748 11.7887 16.0621Z"
                    fill="#112A46"
                />
                <path
                    d="M14.7032 13.8843C14.1204 13.2396 14.4834 12.98 13.2559 12.5728C12.92 12.4615 12.536 12.4338 12.397 12.8159C12.0508 13.7689 13.2022 14.25 13.875 14.5978C14.2876 14.9599 15.0038 14.2172 14.7027 13.8843H14.7032Z"
                    fill="#112A46"
                />
                <path
                    d="M8.30902 17.3579C8.34072 17.3481 8.46802 17.3174 9.23898 17.0742C9.92559 16.8583 9.47672 16.5536 9.05545 16.649C8.56362 16.7598 8.5897 16.4469 8.5897 16.4469C8.69502 16.0002 8.36935 15.7283 8.13162 15.8607C7.86219 16.012 7.77375 15.8104 7.70013 15.7335C7.65923 15.6909 7.53857 15.0072 7.53857 15.0072C7.53244 14.9908 7.51659 14.9651 7.50892 14.9548C7.26863 14.6348 7.13008 15.0036 7.13008 15.0036L7.03295 15.3785L6.93223 15.9602L6.81924 16.4044C6.51352 17.3184 6.91025 16.986 6.91025 16.986L6.90564 17.0101C5.95523 18.6217 7.43479 17.4189 7.43479 17.4189C7.3264 18.0744 7.73029 17.7277 7.90156 17.579C8.05544 17.4451 8.13775 17.4123 8.30902 17.3574V17.3579Z"
                    fill="#112A46"
                />
                <path
                    d="M11.7257 12.0531C11.6306 11.9772 11.5417 11.8941 11.4548 11.8085C11.4261 11.7808 11.3904 11.7305 11.2549 11.6213C11.1705 11.5536 11.031 11.5151 10.9302 11.4905C10.8515 11.4705 10.7712 11.4535 10.69 11.4423C10.5233 11.4207 10.3443 11.432 10.2227 11.5623C10.1577 11.632 10.1301 11.7131 10.1214 11.8059L9.99056 12.2455C9.88116 13.0215 11.2533 13.5227 11.6669 13.5832C11.7769 13.5991 11.8602 13.5334 11.9149 13.4575C11.9814 13.3662 11.9921 13.3221 12.0141 13.2129C12.0412 13.0733 12.0616 12.9307 12.0555 12.7887C12.0504 12.6589 12.0381 12.5209 12.0141 12.3927C11.9834 12.2321 11.8459 12.147 11.7263 12.0521L11.7257 12.0531Z"
                    fill="#112A46"
                />
                <path
                    d="M8.12863 11.2092C7.33568 12.3094 7.89498 12.5777 8.11891 12.4387C8.40521 12.2602 8.67975 12.4212 8.67975 12.4212C9.30552 12.7885 9.44509 12.5448 9.42669 12.3248C9.40828 12.1037 9.86943 11.6467 9.86943 11.6467C10.328 11.0753 9.36278 11.4061 8.86738 11.0727C8.37198 10.7393 8.12914 11.2087 8.12914 11.2087L8.12863 11.2092Z"
                    fill="#112A46"
                />
                <path
                    d="M12.9584 11.9597C13.7836 11.655 13.3311 11.3904 13.4022 11.176C13.4855 10.9277 13.2601 10.4876 12.9303 10.5117C12.6323 10.5338 12.6461 10.1691 12.6461 10.1691C12.644 10.0516 12.9692 9.80185 12.2151 9.70388C11.7698 9.64694 11.5581 9.88494 11.461 10.0891C11.393 10.2312 11.3122 10.364 11.3122 10.364C11.2677 10.4374 11.3189 10.3702 11.21 10.5087C10.6916 11.1672 11.9871 11.8186 12.1205 11.9007C12.3408 12.0372 12.4554 12.1464 12.9584 11.9597Z"
                    fill="#112A46"
                />
                <path
                    d="M15.5475 12.9439C15.7934 12.1955 15.5787 11.9196 15.3696 11.4918C15.16 11.0645 15.3236 10.9548 14.4601 10.9655C13.5966 10.9758 13.6758 11.7083 13.6758 11.7083C13.5526 12.5053 14.1732 12.6495 14.5935 13.3029C14.8818 13.7512 15.2791 13.7625 15.5475 12.9439Z"
                    fill="#112A46"
                />
                <path
                    d="M13.6741 10.6835C14.2477 10.9041 14.6802 10.3204 14.9246 9.8372C15.3096 9.07756 14.5499 9.32069 13.8275 8.92728C13.1158 8.539 12.9747 8.9242 12.9747 8.9242C12.9747 8.9242 12.5759 9.28581 13.0652 9.98697C13.1076 10.0475 13.2523 10.2824 13.3188 10.3383C13.5023 10.4932 13.5253 10.6266 13.6736 10.6835H13.6741Z"
                    fill="#112A46"
                />
                <path
                    d="M10.7651 8.5648C9.70424 9.26853 10.5095 9.5137 10.5095 9.5137C10.6199 9.57628 11.5631 9.84146 11.5826 9.50755C11.6025 9.17415 12.0018 9.19467 12.0018 9.19467C12.716 9.43266 12.8188 8.52018 12.8188 8.52018C12.9711 7.84004 11.8264 7.86159 10.7651 8.5648Z"
                    fill="#112A46"
                />
                <path
                    d="M10.1568 9.70191C9.72535 9.2844 9.2504 9.68447 9.2504 9.68447C8.35878 10.2548 8.73455 10.6842 9.45899 10.8904C10.2289 11.1094 10.2448 11.1135 10.7248 10.6303C11.2054 10.1466 10.8261 9.72551 10.595 9.83373C10.3644 9.94247 10.1574 9.70191 10.1574 9.70191H10.1568Z"
                    fill="#112A46"
                />
                <path
                    d="M13.6808 7.96798C12.664 8.24291 13.5009 8.46244 13.5009 8.46244C13.9682 8.48398 14.5433 8.73634 14.5433 8.73634C15.2775 9.35184 15.281 9.01485 15.2862 8.93125C15.2913 8.84816 14.6977 7.69306 13.6808 7.96798Z"
                    fill="#112A46"
                />
                <path
                    d="M15.7226 10.0957C15.5186 9.37757 15.2042 10.0274 15.2042 10.0274C15.0646 10.2003 15.0743 10.5173 15.471 11.1595C15.9511 11.936 15.9475 10.8866 15.7226 10.0957Z"
                    fill="#112A46"
                />
                <path
                    d="M6.71242 12.5636C6.76508 13.5259 7.08359 12.6272 7.08359 12.6272C7.08359 12.6272 7.62551 11.2624 7.8796 10.8043C8.1342 10.3463 8.86529 9.42098 9.79167 8.71571C9.98697 8.56748 10.1945 8.43309 10.4113 8.31769C10.7339 8.14688 11.0764 8.01917 11.4261 7.91556C11.7656 7.81451 12.1081 7.70269 12.4527 7.62011C12.7068 7.55856 12.9676 7.53497 13.2288 7.52574C13.4957 7.51599 13.7666 7.51035 14.0284 7.57344C14.1347 7.5996 14.2406 7.62935 14.3449 7.66217C14.419 7.68526 14.6756 7.7745 14.7283 7.67089C14.7922 7.54471 14.5033 7.40212 14.4318 7.3616C14.2518 7.26004 14.055 7.16874 13.8571 7.10771C13.7411 7.0718 13.6204 7.05488 13.5003 7.04667C13.0545 7.01589 12.6102 7.08616 12.1664 7.12104C12.0683 7.12822 11.9681 7.15695 11.8714 7.17387C11.4972 7.23953 11.1383 7.35801 10.784 7.47957C10.6245 7.53446 10.4706 7.6037 10.3193 7.67807C9.79781 7.93607 9.25282 8.19612 8.79576 8.55978C8.29525 8.95884 7.81774 9.39328 7.35659 9.83747C7.23338 9.95544 7.12244 10.0857 7.02786 10.2283C6.89903 10.4232 6.7932 10.6417 6.74463 10.8715C6.70066 11.0782 6.70066 11.2772 6.70782 11.4875C6.70782 11.5086 6.70884 11.5296 6.70884 11.5506C6.70782 11.6983 6.69095 11.844 6.69248 11.9917C6.69402 12.1825 6.70271 12.3733 6.71293 12.5636H6.71242Z"
                    fill="#112A46"
                />
                <path
                    d="M16.4835 13.1383C16.5469 12.9572 16.6093 12.7762 16.6594 12.5905C16.6793 12.5187 16.6993 12.4474 16.7161 12.3756C16.7693 12.154 16.7821 11.8965 16.7872 11.6693C16.7903 11.5041 16.7785 11.2277 16.6798 11.0907C16.6379 11.0322 16.4477 10.805 16.3618 10.8773C16.3036 10.9261 16.2872 11.0456 16.2729 11.1148C16.2662 11.1471 16.2606 11.18 16.2555 11.2123C16.188 11.6329 16.2233 12.0612 16.1016 12.4761C16.0479 12.6582 15.9799 12.8347 15.9119 13.0116C15.8276 13.2311 15.7616 13.4558 15.6532 13.6651C15.4145 14.1277 15.1118 14.6406 14.3542 15.4747C14.1624 15.6855 13.961 15.8855 13.7407 16.066C13.4646 16.2917 13.1553 16.4569 12.8409 16.6221C12.3715 16.8683 11.8741 17.0309 11.3736 17.1986C11.2918 17.2258 10.8593 17.3745 11.1226 17.4576C11.28 17.5084 11.4477 17.5053 11.6098 17.5166C11.7785 17.5279 11.9462 17.5422 12.1149 17.5545C12.4078 17.5761 12.665 17.5643 12.9319 17.4248C13.2049 17.2812 13.4385 17.0801 13.6931 16.9098C13.8501 16.8052 14.0024 16.6944 14.1497 16.5764C14.3547 16.4128 14.551 16.2353 14.7274 16.0404C14.9114 15.8388 15.0679 15.6147 15.2335 15.3977C15.6952 14.7966 15.8439 14.4816 15.7212 14.696C15.6006 14.9079 15.958 14.2683 16.0597 14.0457C16.1573 13.8302 16.4048 13.3594 16.4825 13.1368L16.4835 13.1383Z"
                    fill="#112A46"
                />
                <path
                    d="M18.842 13.2857C18.842 13.2857 18.2566 12.4887 17.7612 11.7998C17.6774 11.6829 17.5695 11.5357 17.4294 11.4839C17.2699 11.4254 17.1513 11.508 17.0761 11.6475C17.07 11.6588 17.0644 11.67 17.0588 11.6818C16.9054 11.9963 16.9335 12.3399 16.9754 12.6774C17.0848 13.553 16.7551 13.9746 16.7551 13.9746C16.7551 13.9746 16.1124 14.8214 16.4836 15.2748C16.8548 15.7278 16.5439 16.6623 16.5439 16.6623C16.5071 16.7715 16.5025 16.9167 16.5056 17.0316C16.5092 17.1573 16.525 17.2891 16.6196 17.3809C16.7039 17.463 16.8353 17.5071 16.9493 17.5199C17.1508 17.543 17.3338 17.485 17.5041 17.3788C17.6682 17.2778 17.8042 17.1373 17.9407 17.0034C18.0899 16.8577 18.2034 16.6833 18.3338 16.5187C18.6651 16.1006 18.9023 15.7565 18.9908 15.3056C19.0787 14.8558 19.0853 14.9414 19.1334 14.5639C19.1651 14.3141 19.0813 13.7474 18.9877 13.5089C18.9529 13.4186 18.8962 13.3601 18.8415 13.2852L18.842 13.2857ZM17.1446 15.677C17.1446 15.677 17.1273 15.2831 17.0265 15.0958C17.0265 15.0958 17.1861 14.1705 17.5184 14.7999C17.8502 15.4292 17.1446 15.677 17.1446 15.677ZM18.4131 15.0784C18.2162 15.3133 18.0117 14.8266 17.9519 14.6942C17.5843 13.8823 17.5322 13.5853 17.5322 13.5853C17.5322 13.5853 17.3752 13.0867 17.6692 13.3837C17.8808 13.5981 18.0219 13.9012 18.16 14.1649C18.2709 14.3762 18.6206 14.8301 18.4131 15.0784Z"
                    fill="#112A46"
                />
                <path
                    d="M7.58815 6.6376C7.75482 6.74275 8.07793 6.75711 8.26402 6.70736C8.40359 6.66889 8.53652 6.60991 8.67762 6.574C9.09889 6.46885 9.58918 6.53092 9.85401 6.91304C9.97926 7.09256 10.1756 7.30645 10.4133 7.29978C10.6275 7.29414 10.8014 7.1536 10.9818 7.05615C11.171 6.95408 11.3755 6.87098 11.579 6.81559C11.9793 6.70736 12.4005 6.66222 12.8141 6.67966C12.9813 6.68684 13.1449 6.70736 13.3095 6.73557C13.4936 6.76789 13.7482 6.81918 13.9312 6.75711C14.3167 6.62683 13.6393 5.99953 13.5268 5.89695C13.3131 5.70204 13.0897 5.51533 12.8509 5.35171C12.4256 5.05986 11.9583 4.8552 11.4583 4.73108C11.0391 4.62644 10.603 4.55104 10.1689 4.53976C9.66689 4.52642 8.91177 4.70082 7.88672 5.39685C7.75737 5.48456 7.62547 5.58253 7.51964 5.69845C7.41177 5.81744 7.30492 5.96414 7.29674 6.1293C7.2906 6.26368 7.36525 6.4191 7.46647 6.53194C7.50379 6.57451 7.54572 6.61042 7.58815 6.6376ZM11.0309 5.17116C11.7605 5.52405 11.9767 5.70358 11.9767 5.70358C11.9767 5.70358 12.3832 5.96209 12.0186 5.92823C11.7564 5.90464 11.4813 5.77487 11.2339 5.67075C11.035 5.58766 10.2528 5.47379 10.2247 5.1727C10.1981 4.88752 10.9118 5.11269 11.0309 5.17065V5.17116ZM10.7891 6.09647C11.3003 6.59042 10.3699 6.48886 10.3699 6.48886C10.218 6.34011 9.84531 6.21393 9.84531 6.21393C9.84531 6.21393 10.2783 5.60253 10.7891 6.09647Z"
                    fill="#112A46"
                />
                <path
                    d="M19.6863 6.9726C19.5749 6.88232 19.4527 6.80334 19.3284 6.72999C19.2691 6.69511 19.1996 6.66792 19.1429 6.62843C19.1009 6.59919 19.0467 6.58278 18.9987 6.56637C18.8458 6.51353 18.6899 6.47199 18.5334 6.42993C18.3494 6.38018 18.1076 6.34735 17.9169 6.33042C17.6076 6.30272 17.3223 6.37248 17.0258 6.44634C16.8985 6.47814 16.7717 6.51918 16.6521 6.57406C16.4977 6.64536 16.3509 6.73409 16.2277 6.85411C16.1853 6.89566 16.1464 6.94285 16.1117 6.99004C16.0646 7.05364 16.0125 7.11365 15.9726 7.18136C15.9624 7.1988 15.943 7.24445 15.9297 7.25983C15.8852 7.31318 15.8632 7.3419 15.8157 7.39217C15.7405 7.47321 15.6679 7.5563 15.5999 7.6435C15.4726 7.80558 15.3499 8.0246 15.3182 8.23233C15.2932 8.38929 15.3463 8.54367 15.4373 8.67088C15.5304 8.79962 15.6766 8.87605 15.7686 9.0053C15.8872 9.17098 15.9516 9.39051 16.0084 9.58336C16.0437 9.70134 16.0759 9.82033 16.1019 9.94087C16.1009 10.0619 16.1152 10.1973 16.1602 10.3102C16.2083 10.4297 16.31 10.5436 16.4409 10.5712C16.6367 10.6133 16.7052 10.422 16.7855 10.3076C16.8514 10.2132 16.8995 10.1348 16.9992 10.0783C17.0917 10.026 17.1843 10.0306 17.286 10.0291C17.3612 10.0276 17.4399 10.027 17.5145 10.0168C17.7973 9.97934 18.0902 9.80495 18.332 9.65671C18.5227 9.53977 18.7369 9.3941 18.8509 9.19457C18.9031 9.1043 18.9741 9.02479 19.0478 8.95145C19.1919 8.80885 19.353 8.68421 19.5191 8.56778C19.6919 8.44724 19.7241 8.36826 19.8494 8.19745C19.9189 8.10205 19.9726 7.96869 19.989 7.85072C20.0355 7.51732 19.9332 7.1752 19.6863 6.97414V6.9726ZM16.7523 7.7071C17.1055 7.2583 17.9051 7.29882 17.9051 7.29882C17.1878 7.33062 16.9966 7.80558 16.9966 7.80558C16.4251 8.24823 16.7517 7.7071 16.7517 7.7071H16.7523ZM16.9465 8.06307C17.3719 7.53117 17.7988 7.57579 17.7988 7.57579C17.5651 7.56502 17.2937 8.15437 17.2937 8.15437C16.6429 8.64369 16.9465 8.06307 16.9465 8.06307ZM18.1337 8.81552C18.1337 8.81552 17.8049 8.84732 17.8111 9.01864C17.8167 9.18944 17.3504 9.25817 17.5595 8.81706C17.7098 8.50059 18.1602 8.58522 18.1602 8.58522C18.1602 8.58522 18.741 8.68678 18.1337 8.81552Z"
                    fill="#112A46"
                />
            </g>
            <defs>
                <clipPath id="clip0_1248_24845">
                    <rect width="16" height="16" fill="white" transform="translate(4 4.53906)" />
                </clipPath>
            </defs>
        </Icon>
    )
}

export const StoreOutletIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const generatedId = useId()
    const iconId = `storeOutletIcon-${generatedId}`
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Outlet store icon',
            id: 'stores.outlet.icon.svg.title'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'outlet pictogram',
            id: 'stores.outlet.icon.svg.description'
        })

    return (
        <Icon viewBox="0 0 24 25" fill="none" role="img" aria-labelledby={iconId} {...props}>
            <title id={iconId}>{title}</title>
            <desc>{description}</desc>
            <rect x="0.5" y="1.03906" width="23" height="23" rx="11.5" stroke="#112A46" />
            <path
                d="M14.1736 14.1699V13.3359H18.5551V14.1699H16.8577V18.8276H15.8709V14.1699H14.1736Z"
                fill="black"
            />
            <path
                d="M9.75293 18.8276V13.3359H13.3246V14.1699H10.7477V15.6608H13.1396V16.4947H10.7477V17.9936H13.3461V18.8276H9.75293Z"
                fill="black"
            />
            <path
                d="M5.40039 18.8276V13.3359H6.39521V17.9936H8.81388V18.8276H5.40039Z"
                fill="black"
            />
            <path
                d="M15.4346 7.15815V6.32422H19.8161V7.15815H18.1187V11.8158H17.1319V7.15815H15.4346Z"
                fill="black"
            />
            <path
                d="M13.5243 6.32422H14.5191V9.912C14.5191 10.3053 14.4261 10.6512 14.2402 10.9497C14.0561 11.2483 13.7969 11.4815 13.4626 11.6496C13.1283 11.8158 12.7377 11.899 12.2908 11.899C11.8421 11.899 11.4506 11.8158 11.1163 11.6496C10.782 11.4815 10.5228 11.2483 10.3387 10.9497C10.1546 10.6512 10.0625 10.3053 10.0625 9.912V6.32422H11.0573V9.82888C11.0573 10.0577 11.1074 10.2615 11.2075 10.4402C11.3094 10.619 11.4524 10.7593 11.6365 10.8612C11.8206 10.9613 12.0387 11.0114 12.2908 11.0114C12.5428 11.0114 12.7609 10.9613 12.9451 10.8612C13.131 10.7593 13.274 10.619 13.3741 10.4402C13.4742 10.2615 13.5243 10.0577 13.5243 9.82888V6.32422Z"
                fill="black"
            />
            <path
                d="M9.04649 9.07089C9.04649 9.66259 8.93566 10.1694 8.71399 10.5913C8.49411 11.0114 8.19379 11.3331 7.81303 11.5566C7.43405 11.78 7.00412 11.8918 6.52325 11.8918C6.04237 11.8918 5.61155 11.78 5.23079 11.5566C4.85181 11.3314 4.55149 11.0087 4.32982 10.5886C4.10994 10.1667 4 9.66081 4 9.07089C4 8.47918 4.10994 7.97328 4.32982 7.55319C4.55149 7.1313 4.85181 6.80864 5.23079 6.58518C5.61155 6.36173 6.04237 6.25 6.52325 6.25C7.00412 6.25 7.43405 6.36173 7.81303 6.58518C8.19379 6.80864 8.49411 7.1313 8.71399 7.55319C8.93566 7.97328 9.04649 8.47918 9.04649 9.07089ZM8.04631 9.07089C8.04631 8.65437 7.98106 8.3031 7.85057 8.01708C7.72186 7.72927 7.54309 7.51207 7.31428 7.36548C7.08546 7.21711 6.82178 7.14292 6.52325 7.14292C6.22471 7.14292 5.96104 7.21711 5.73222 7.36548C5.5034 7.51207 5.32374 7.72927 5.19325 8.01708C5.06454 8.3031 5.00018 8.65437 5.00018 9.07089C5.00018 9.48741 5.06454 9.83957 5.19325 10.1274C5.32374 10.4134 5.5034 10.6306 5.73222 10.779C5.96104 10.9256 6.22471 10.9989 6.52325 10.9989C6.82178 10.9989 7.08546 10.9256 7.31428 10.779C7.54309 10.6306 7.72186 10.4134 7.85057 10.1274C7.98106 9.83957 8.04631 9.48741 8.04631 9.07089Z"
                fill="black"
            />
        </Icon>
    )
}

export const StoreOthersIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const generatedId = useId()
    const iconId = `storeOthersIcon-${generatedId}`
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Other stores icon',
            id: 'stores.other.icon.svg.title'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Other stores pictogram',
            id: 'stores.other.icon.svg.description'
        })

    return (
        <Icon viewBox="0 0 24 25" fill="none" role="img" aria-labelledby={iconId} {...props}>
            <title id={iconId}>{title}</title>
            <desc>{description}</desc>
            <rect x="0.5" y="1.03906" width="23" height="23" rx="11.5" stroke="#112A46" />
            <path
                d="M12.8137 18.9787L11.9518 14.4807L11.0425 18.9787H4.62793L6.49487 6.10155L12.1436 6.10156H17.3136L19.372 18.9787H12.8137Z"
                stroke="#112A46"
            />
            <path d="M10.6099 9.65958L11.9418 6.84766L13.39 9.63984" stroke="#112A46" />
        </Icon>
    )
}

export const StoreMarkerIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 49 49" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <circle cx="24.5" cy="24.5391" r="24" fill="#143A64" />
            <path
                d="M10.3864 27.0028C7.92005 28.6459 8.01279 29.3709 7.95348 30.2657C7.8877 31.2199 7.0228 33.4957 8.39887 33.8142C9.77385 34.1326 12.6403 31.6477 12.9951 31.2486C13.351 30.8495 13.2356 29.9441 13.4728 28.1141C13.9764 24.2281 10.3864 27.0028 10.3864 27.0028ZM10.0683 31.4503C10.0219 31.6233 9.67248 32.4597 9.63905 31.1552C9.60885 29.9664 9.79757 29.3571 9.79757 29.3571C9.79757 29.3571 10.0478 28.3179 10.1524 29.528C10.2785 30.9684 10.1146 31.2783 10.0693 31.4503H10.0683ZM11.6924 29.0132C11.6622 30.2041 11.4432 30.8017 11.4432 30.8017C11.4432 30.8017 11.1413 31.8282 11.0971 30.6149C11.0442 29.1692 11.2243 28.8677 11.2793 28.6979C11.3343 28.5281 11.7258 27.7108 11.6934 29.0121L11.6924 29.0132Z"
                fill="white"
            />
            <path
                d="M18.9384 34.7414L16.4807 36.3506C15.3452 37.0055 15.4077 38.913 15.7248 40.537C16.0731 42.3256 20.1161 39.2845 20.6132 38.8864C21.1104 38.4894 21.3541 38.3769 21.4253 35.6978C21.4975 33.0165 18.9374 34.7414 18.9374 34.7414H18.9384ZM17.695 36.9036C18.7799 36.3782 19.4205 36.2922 19.4205 36.2922C19.4205 36.2922 20.4838 36.0799 19.4129 36.6818C18.135 37.3982 17.7813 37.3813 17.6034 37.412C17.4254 37.4428 16.5066 37.48 17.695 36.9036ZM19.4701 38.2272C18.1652 38.896 17.8126 38.8652 17.6336 38.8896C17.4545 38.914 16.5357 38.9183 17.7446 38.3875C18.8489 37.9014 19.4927 37.8388 19.4927 37.8388C19.4927 37.8388 20.5636 37.6657 19.4701 38.2272Z"
                fill="white"
            />
            <path
                d="M22.1444 30.4478C20.8762 28.7898 19.9725 30.4478 19.9725 30.4478C17.9731 32.8775 20.0803 32.7194 21.2752 33.4475C22.3773 34.1194 22.6426 33.7999 23.5151 33.4634C24.3875 33.128 23.4126 32.1058 22.1444 30.4468V30.4478Z"
                fill="white"
            />
            <path
                d="M15.8133 29.9016C15.9437 30.2848 16.1648 30.5618 16.5336 30.7476C16.9014 30.9344 17.3058 31.0681 17.6908 31.2188C18.01 31.3441 18.3443 31.4545 18.6592 31.2592C19.0086 31.0426 19.2588 30.6021 19.3321 30.2115C19.5133 29.2541 18.217 29.2689 17.7026 28.7743C17.4374 28.5185 17.3705 28.1703 17.1677 27.8784C16.9575 27.5748 16.5843 27.5908 16.2565 27.6704C15.8909 27.7606 15.3927 27.9506 15.2126 28.3062C15.0044 28.718 15.5544 29.5396 15.6763 29.6723C15.713 29.7827 15.7723 29.7795 15.8122 29.9016H15.8133Z"
                fill="white"
            />
            <path
                d="M22.7117 29.0966C22.7117 29.0966 23.7632 27.5055 22.6535 27.0883C20.6272 26.3294 21.3648 26.3644 20.7393 26.0725C20.1138 25.7806 20.3489 25.3178 18.9038 25.5991C18.4908 25.6797 18.2902 25.8793 18.2902 25.8793C17.8179 26.3485 17.5138 28.1837 18.3291 28.4587C20.639 29.2388 21.602 29.3683 21.602 29.3683C22.647 29.4734 22.7117 29.0966 22.7117 29.0966Z"
                fill="white"
            />
            <path
                d="M16.0556 27.2343C16.8148 27.3245 17.5546 26.1484 17.5212 25.4733C17.4878 24.7993 17.4597 24.898 16.1247 24.8343C14.7874 24.7707 14.8413 26.0465 14.8413 26.0465C13.9538 27.4296 15.2964 27.1441 16.0556 27.2354V27.2343Z"
                fill="white"
            />
            <path
                d="M24.0545 31.8355C25.4446 33.7005 27.0643 31.4905 28.1201 30.4662C29.1759 29.4408 27.2682 28.9844 25.5254 27.9049C23.7838 26.8265 23.5908 28.3539 23.5908 28.3539C22.4228 31.2761 22.838 30.2061 24.0545 31.8355Z"
                fill="white"
            />
            <path
                d="M30.202 27.3218C28.9726 25.9875 29.7383 25.4504 27.149 24.6076C26.4405 24.3773 25.6306 24.32 25.3372 25.1108C24.6071 27.083 27.0357 28.0786 28.4549 28.7983C29.3252 29.5477 30.8361 28.0107 30.2009 27.3218H30.202Z"
                fill="white"
            />
            <path
                d="M16.7146 34.5128C16.7814 34.4926 17.05 34.429 18.6762 33.9258C20.1245 33.4789 19.1777 32.8484 18.2891 33.0459C17.2516 33.2751 17.3066 32.6277 17.3066 32.6277C17.5288 31.7031 16.8418 31.1405 16.3404 31.4144C15.772 31.7275 15.5855 31.3104 15.4302 31.1512C15.3439 31.0631 15.0894 29.6481 15.0894 29.6481C15.0765 29.6142 15.043 29.5611 15.0269 29.5399C14.52 28.8775 14.2278 29.6407 14.2278 29.6407L14.0229 30.4166L13.8104 31.6203L13.5721 32.5396C12.9272 34.4311 13.764 33.7433 13.764 33.7433L13.7543 33.7931C11.7495 37.1283 14.8705 34.6391 14.8705 34.6391C14.6419 35.9957 15.4938 35.2781 15.8551 34.9703C16.1797 34.6933 16.3533 34.6253 16.7146 34.5117V34.5128Z"
                fill="white"
            />
            <path
                d="M23.9215 23.5399C23.7209 23.3828 23.5333 23.2109 23.3499 23.0336C23.2895 22.9763 23.2141 22.8723 22.9283 22.6462C22.7503 22.5061 22.4559 22.4265 22.2435 22.3755C22.0774 22.3341 21.9081 22.2991 21.7366 22.2757C21.3851 22.2311 21.0076 22.2545 20.7509 22.5241C20.614 22.6685 20.5558 22.8362 20.5374 23.0283L20.2613 23.938C20.0306 25.544 22.925 26.581 23.7975 26.7063C24.0293 26.7392 24.2051 26.6033 24.3205 26.4462C24.4607 26.2573 24.4834 26.166 24.5297 25.9399C24.5869 25.6512 24.63 25.3561 24.6171 25.0621C24.6063 24.7935 24.5804 24.508 24.5297 24.2426C24.465 23.9104 24.1749 23.7342 23.9226 23.5378L23.9215 23.5399Z"
                fill="white"
            />
            <path
                d="M16.3339 21.7877C14.6613 24.0645 15.8411 24.6197 16.3134 24.332C16.9174 23.9626 17.4965 24.2959 17.4965 24.2959C18.8164 25.056 19.1109 24.5518 19.072 24.0964C19.0332 23.6389 20.0059 22.6931 20.0059 22.6931C20.9733 21.5107 18.9372 22.1953 17.8922 21.5054C16.8473 20.8154 16.335 21.7867 16.335 21.7867L16.3339 21.7877Z"
                fill="white"
            />
            <path
                d="M26.5216 23.3423C28.2622 22.7118 27.3078 22.1641 27.4577 21.7204C27.6334 21.2066 27.1579 20.2959 26.4623 20.3458C25.8336 20.3914 25.8627 19.6367 25.8627 19.6367C25.8584 19.3936 26.5442 18.8767 24.9536 18.674C24.0143 18.5562 23.5678 19.0487 23.3629 19.4711C23.2195 19.7652 23.0491 20.0401 23.0491 20.0401C22.9553 20.1919 23.0631 20.0528 22.8334 20.3394C21.7399 21.7023 24.4726 23.0504 24.7541 23.2202C25.2189 23.5026 25.4604 23.7287 26.5216 23.3423Z"
                fill="white"
            />
            <path
                d="M31.9826 25.3807C32.5013 23.832 32.0484 23.2609 31.6073 22.3757C31.1651 21.4915 31.5102 21.2643 29.6888 21.2866C27.8673 21.3078 28.0345 22.8236 28.0345 22.8236C27.7746 24.4731 29.0838 24.7714 29.9703 26.1237C30.5785 27.0514 31.4164 27.0748 31.9826 25.3807Z"
                fill="white"
            />
            <path
                d="M28.0313 20.7003C29.2413 21.1567 30.1536 19.9487 30.6691 18.9488C31.4812 17.3768 29.8786 17.88 28.3548 17.0658C26.8537 16.2623 26.556 17.0594 26.556 17.0594C26.556 17.0594 25.7149 17.8078 26.7469 19.2588C26.8364 19.384 27.1416 19.8702 27.2818 19.9859C27.669 20.3065 27.7175 20.5824 28.0302 20.7003H28.0313Z"
                fill="white"
            />
            <path
                d="M21.8956 16.3156C19.6578 17.772 21.3563 18.2793 21.3563 18.2793C21.5893 18.4088 23.579 18.9576 23.6199 18.2666C23.662 17.5767 24.5042 17.6191 24.5042 17.6191C26.0108 18.1116 26.2276 16.2233 26.2276 16.2233C26.5489 14.8158 24.1343 14.8604 21.8956 16.3156Z"
                fill="white"
            />
            <path
                d="M20.6121 18.667C19.702 17.803 18.7001 18.6309 18.7001 18.6309C16.8193 19.8113 17.612 20.6997 19.1401 21.1264C20.7642 21.5797 20.7976 21.5882 21.8103 20.5883C22.824 19.5873 22.0238 18.7159 21.5363 18.9398C21.05 19.1649 20.6132 18.667 20.6132 18.667H20.6121Z"
                fill="white"
            />
            <path
                d="M28.0452 15.0795C25.9002 15.6485 27.6656 16.1028 27.6656 16.1028C28.6513 16.1474 29.8645 16.6696 29.8645 16.6696C31.4131 17.9433 31.4206 17.246 31.4314 17.0729C31.4422 16.901 30.1902 14.5106 28.0452 15.0795Z"
                fill="white"
            />
            <path
                d="M32.3526 19.4843C31.9223 17.9983 31.2591 19.3431 31.2591 19.3431C30.9646 19.7009 30.9851 20.3568 31.822 21.6858C32.8346 23.2928 32.8271 21.1211 32.3526 19.4843Z"
                fill="white"
            />
            <path
                d="M13.3465 24.593C13.4575 26.5843 14.1294 24.7246 14.1294 24.7246C14.1294 24.7246 15.2725 21.9001 15.8085 20.9522C16.3455 20.0043 17.8877 18.0894 19.8418 16.6299C20.2537 16.3231 20.6916 16.045 21.1488 15.8062C21.8293 15.4527 22.5518 15.1884 23.2895 14.974C24.0056 14.7649 24.7281 14.5335 25.4549 14.3626C25.9909 14.2352 26.5409 14.1864 27.092 14.1673C27.6549 14.1471 28.2265 14.1355 28.7786 14.266C29.0029 14.3202 29.2262 14.3817 29.4462 14.4497C29.6025 14.4974 30.1439 14.6821 30.255 14.4677C30.3898 14.2066 29.7805 13.9115 29.6295 13.8276C29.2499 13.6175 28.8347 13.4285 28.4174 13.3022C28.1726 13.2279 27.9181 13.1929 27.6646 13.1759C26.7242 13.1122 25.7871 13.2576 24.851 13.3298C24.644 13.3447 24.4326 13.4041 24.2288 13.4391C23.4394 13.575 22.6823 13.8202 21.935 14.0718C21.5985 14.1853 21.2739 14.3286 20.9547 14.4826C19.8547 15.0165 18.7051 15.5546 17.741 16.3072C16.6852 17.133 15.678 18.0321 14.7053 18.9513C14.4454 19.1954 14.2114 19.4651 14.0118 19.7601C13.7401 20.1635 13.5169 20.6157 13.4144 21.0912C13.3217 21.519 13.3217 21.9308 13.3368 22.366C13.3368 22.4096 13.3389 22.4531 13.3389 22.4966C13.3368 22.8023 13.3012 23.1038 13.3044 23.4095C13.3076 23.8043 13.326 24.1992 13.3475 24.593H13.3465Z"
                fill="white"
            />
            <path
                d="M33.9571 25.7822C34.0908 25.4076 34.2224 25.0329 34.3281 24.6486C34.3702 24.5 34.4122 24.3525 34.4478 24.2039C34.56 23.7453 34.5869 23.2124 34.5977 22.7422C34.6042 22.4004 34.5794 21.8283 34.3712 21.5449C34.2828 21.4239 33.8816 20.9537 33.7005 21.1033C33.5775 21.2042 33.543 21.4515 33.5128 21.5948C33.4988 21.6617 33.4869 21.7296 33.4762 21.7965C33.3338 22.6669 33.4082 23.5532 33.1515 24.4119C33.0383 24.7887 32.8949 25.1539 32.7515 25.5201C32.5735 25.9744 32.4344 26.4393 32.2058 26.8724C31.7022 27.8298 31.0637 28.8913 29.4655 30.6172C29.0611 31.0535 28.6362 31.4674 28.1714 31.8411C27.5891 32.3081 26.9366 32.6499 26.2734 32.9917C25.2834 33.5012 24.2341 33.8377 23.1783 34.1848C23.0058 34.241 22.0934 34.5489 22.6488 34.7208C22.981 34.8259 23.3347 34.8195 23.6766 34.8429C24.0324 34.8662 24.3862 34.896 24.742 34.9214C25.36 34.966 25.9024 34.9416 26.4654 34.6529C27.0412 34.3557 27.5341 33.9396 28.0711 33.5872C28.4022 33.3706 28.7236 33.1414 29.0341 32.8972C29.4666 32.5586 29.8807 32.1914 30.2528 31.788C30.641 31.3708 30.971 30.907 31.3204 30.458C32.2942 29.214 32.608 28.5622 32.3492 29.0059C32.0947 29.4443 32.8485 28.1206 33.0631 27.66C33.2691 27.2142 33.791 26.2397 33.955 25.7791L33.9571 25.7822Z"
                fill="white"
            />
            <path
                d="M38.9323 26.0847C38.9323 26.0847 37.6976 24.4351 36.6526 23.0096C36.4757 22.7676 36.2482 22.463 35.9527 22.3557C35.6162 22.2347 35.366 22.4056 35.2075 22.6943C35.1946 22.7177 35.1827 22.7411 35.1708 22.7655C34.8473 23.4161 34.9066 24.1273 34.995 24.8258C35.2258 26.6377 34.5302 27.5102 34.5302 27.5102C34.5302 27.5102 33.1747 29.2627 33.9576 30.201C34.7405 31.1383 34.0849 33.0723 34.0849 33.0723C34.0072 33.2984 33.9975 33.5987 34.004 33.8365C34.0115 34.0966 34.045 34.3694 34.2445 34.5594C34.4224 34.7292 34.6996 34.8205 34.94 34.847C35.3649 34.8948 35.751 34.7748 36.1101 34.5551C36.4563 34.346 36.7432 34.0552 37.0311 33.7781C37.346 33.4767 37.5854 33.1158 37.8604 32.7751C38.5592 31.91 39.0596 31.1977 39.2462 30.2647C39.4317 29.3338 39.4457 29.5111 39.547 28.7298C39.6139 28.2129 39.437 27.04 39.2397 26.5464C39.1664 26.3596 39.0467 26.2386 38.9313 26.0836L38.9323 26.0847ZM35.352 31.0332C35.352 31.0332 35.3153 30.218 35.1029 29.8306C35.1029 29.8306 35.4394 27.9157 36.1403 29.2181C36.8402 30.5205 35.352 31.0332 35.352 31.0332ZM38.0276 29.7945C37.6124 30.2806 37.181 29.2733 37.0548 28.9994C36.2794 27.3191 36.1694 26.7046 36.1694 26.7046C36.1694 26.7046 35.8384 25.6728 36.4585 26.2874C36.9049 26.7311 37.2026 27.3584 37.4937 27.904C37.7278 28.3413 38.4654 29.2807 38.0276 29.7945Z"
                fill="white"
            />
            <path
                d="M15.1941 12.3272C15.5456 12.5448 16.2272 12.5745 16.6197 12.4716C16.9141 12.3919 17.1945 12.2699 17.4922 12.1956C18.3808 11.978 19.415 12.1064 19.9736 12.8972C20.2378 13.2687 20.6519 13.7113 21.1534 13.6975C21.6052 13.6859 21.9719 13.395 22.3526 13.1933C22.7516 12.9821 23.183 12.8102 23.6122 12.6955C24.4566 12.4716 25.3452 12.3781 26.2176 12.4142C26.5703 12.4291 26.9154 12.4716 27.2626 12.5299C27.6509 12.5968 28.1879 12.703 28.574 12.5745C29.3871 12.3049 27.9582 11.0067 27.721 10.7944C27.2702 10.3911 26.7989 10.0047 26.2953 9.66611C25.398 9.06214 24.4124 8.63862 23.3577 8.38174C22.4734 8.1652 21.5535 8.00917 20.6379 7.98582C19.5789 7.95822 17.9861 8.31912 15.8239 9.75952C15.551 9.94103 15.2728 10.1438 15.0495 10.3837C14.822 10.6299 14.5966 10.9335 14.5794 11.2753C14.5664 11.5534 14.7239 11.875 14.9374 12.1085C15.0161 12.1966 15.1045 12.2709 15.1941 12.3272ZM22.4561 9.29248C23.995 10.0228 24.4512 10.3943 24.4512 10.3943C24.4512 10.3943 25.3085 10.9293 24.5396 10.8592C23.9864 10.8104 23.4062 10.5418 22.8843 10.3263C22.4647 10.1544 20.8148 9.91874 20.7555 9.29566C20.6994 8.70549 22.2049 9.17147 22.4561 9.29141V9.29248ZM21.946 11.2074C23.0244 12.2295 21.0617 12.0194 21.0617 12.0194C20.7414 11.7115 19.9553 11.4504 19.9553 11.4504C19.9553 11.4504 20.8687 10.1852 21.946 11.2074Z"
                fill="white"
            />
            <path
                d="M40.7134 13.0215C40.4783 12.8347 40.2206 12.6712 39.9585 12.5194C39.8334 12.4472 39.6867 12.391 39.567 12.3092C39.4786 12.2487 39.3643 12.2148 39.2629 12.1808C38.9405 12.0715 38.6116 11.9855 38.2816 11.8984C37.8933 11.7955 37.3832 11.7276 36.981 11.6925C36.3285 11.6352 35.7268 11.7796 35.1013 11.9324C34.8328 11.9982 34.5653 12.0831 34.313 12.1967C33.9873 12.3443 33.6778 12.5279 33.4179 12.7763C33.3284 12.8623 33.2464 12.9599 33.1731 13.0576C33.0739 13.1892 32.9639 13.3134 32.8798 13.4535C32.8582 13.4896 32.8172 13.5841 32.7892 13.6159C32.6954 13.7263 32.649 13.7857 32.5487 13.8898C32.3902 14.0575 32.237 14.2294 32.0936 14.4099C31.8251 14.7453 31.5663 15.1985 31.4994 15.6284C31.4466 15.9532 31.5587 16.2727 31.7507 16.536C31.9469 16.8024 32.2554 16.9606 32.4495 17.228C32.6997 17.5709 32.8356 18.0252 32.9553 18.4243C33.0297 18.6685 33.0976 18.9147 33.1526 19.1642C33.1505 19.4147 33.1806 19.6949 33.2755 19.9284C33.3769 20.1757 33.5915 20.4114 33.8676 20.4687C34.2806 20.5557 34.4251 20.1598 34.5945 19.9231C34.7336 19.7278 34.8349 19.5654 35.0452 19.4486C35.2404 19.3404 35.4356 19.3499 35.6502 19.3467C35.8088 19.3435 35.9748 19.3425 36.1323 19.3213C36.7286 19.2438 37.3466 18.8829 37.8567 18.5761C38.2589 18.3341 38.7108 18.0326 38.9513 17.6197C39.0613 17.4329 39.2112 17.2684 39.3665 17.1166C39.6706 16.8215 40.0103 16.5636 40.3608 16.3226C40.7253 16.0732 40.7932 15.9097 41.0574 15.5562C41.2041 15.3588 41.3173 15.0828 41.3518 14.8387C41.45 14.1487 41.2343 13.4408 40.7134 13.0247V13.0215ZM34.5244 14.5415C35.2695 13.6127 36.9562 13.6966 36.9562 13.6966C35.4432 13.7624 35.0398 14.7453 35.0398 14.7453C33.8342 15.6613 34.5233 14.5415 34.5233 14.5415H34.5244ZM34.9342 15.2781C35.8314 14.1774 36.7319 14.2698 36.7319 14.2698C36.239 14.2475 35.6664 15.4671 35.6664 15.4671C34.2936 16.4797 34.9342 15.2781 34.9342 15.2781ZM37.4382 16.8353C37.4382 16.8353 36.7448 16.9011 36.7578 17.2556C36.7696 17.6091 35.7861 17.7513 36.2272 16.8385C36.5442 16.1836 37.4943 16.3587 37.4943 16.3587C37.4943 16.3587 38.7194 16.5689 37.4382 16.8353Z"
                fill="white"
            />
        </Icon>
    )
}

export const SuccessIcon = ({svgTitle, svgDescription, ...props}) => {
    // Use the useFormatMessage hook to get the default title and description if intl avaliable
    const defaultTitle = useFormatMessage(DEFAULT_ICON_META.SuccessIcon.title)
    const defaultDescription = useFormatMessage(DEFAULT_ICON_META.SuccessIcon.description)

    // Use provided svgTitle and svgDescription or fall back to defaults
    const title = svgTitle || defaultTitle
    const description = svgDescription || defaultDescription

    return (
        <Icon viewBox="0 0 16 16" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <circle cx="8" cy="8" r="8" fill="currentColor" />
            <path stroke="#fff" strokeWidth="1.1" d="m3 8.278 3.056 3.055L13 4.39" />
        </Icon>
    )
}

/**
 * TurtleIcon is a React functional component that renders a custom SVG icon
 * representing a turtle. It allows for customization of the SVG's title and
 * description for accessibility purposes.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.svgTitle] - Optional title for the SVG, used for accessibility.
 *                                     If not provided, a default title is used.
 * @param {string} [props.svgDescription] - Optional description for the SVG, used for accessibility.
 *                                          If not provided, a default description is used.
 * @returns {JSX.Element} A JSX element representing the turtle icon.
 *
 * The component utilizes the `useFormatMessage` hook to retrieve default
 * internationalized titles and descriptions if available. The icon is rendered
 * using an SVG path with a viewBox of "0 0 32 32" and a fill color of black.
 */
export const TurtleIcon = ({svgTitle, svgDescription, color = '#000', ...props}) => {
    // Use the useFormatMessage hook to get the default title and description if intl avaliable
    const defaultTitle = useFormatMessage(DEFAULT_ICON_META.SuccessIcon.title)
    const defaultDescription = useFormatMessage(DEFAULT_ICON_META.SuccessIcon.description)

    // Use provided svgTitle and svgDescription or fall back to defaults
    const title = svgTitle || defaultTitle
    const description = svgDescription || defaultDescription
    return (
        <Icon viewBox="0 0 32 32" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill={color}
                d="M3.455 18.187c-2.193 1.461-2.11 2.105-2.163 2.9-.058.849-.827 2.872.396 3.155 1.222.283 3.77-1.926 4.085-2.28.317-.355.214-1.16.425-2.787.448-3.454-2.743-.988-2.743-.988Zm-.283 3.954c-.041.154-.352.897-.382-.262-.027-1.057.141-1.599.141-1.599s.223-.924.316.152c.112 1.28-.034 1.556-.074 1.709h-.001Zm1.443-2.166c-.026 1.058-.221 1.59-.221 1.59s-.268.912-.308-.167c-.047-1.285.113-1.553.162-1.704.05-.15.397-.877.368.28ZM11.056 25.068l-2.184 1.43c-1.01.582-.954 2.278-.672 3.721.31 1.59 3.903-1.113 4.345-1.467.442-.353.659-.453.722-2.834.064-2.384-2.212-.85-2.212-.85h.001ZM9.951 26.99c.964-.467 1.534-.544 1.534-.544s.945-.189-.007.346c-1.136.637-1.45.622-1.608.65-.159.027-.975.06.081-.452Zm1.578 1.176c-1.16.595-1.473.567-1.633.589-.159.022-.975.025.1-.446.98-.433 1.553-.488 1.553-.488s.952-.154-.02.345ZM13.906 21.252c-1.127-1.474-1.93 0-1.93 0-1.778 2.16.095 2.02 1.158 2.667.98.597 1.215.313 1.99.014.776-.299-.09-1.207-1.218-2.682v.001ZM8.278 20.764c.116.34.313.587.64.752.328.166.687.285 1.03.42.283.11.58.209.86.035.31-.192.533-.584.598-.931.161-.851-.99-.838-1.448-1.278-.236-.227-.295-.537-.476-.796-.186-.27-.518-.256-.81-.185-.325.08-.767.25-.927.565-.186.366.303 1.097.412 1.215.032.098.085.095.12.203h.001ZM14.41 20.05s.935-1.414-.051-1.785c-1.802-.675-1.146-.643-1.702-.903s-.347-.67-1.631-.42c-.368.07-.546.248-.546.248-.42.417-.69 2.049.035 2.293 2.053.694 2.909.809 2.909.809.929.093.986-.242.986-.242ZM8.494 18.396c.675.08 1.332-.965 1.303-1.565-.03-.6-.055-.512-1.242-.568-1.188-.057-1.14 1.077-1.14 1.077-.79 1.23.404.976 1.079 1.057v-.001ZM15.604 22.484c1.236 1.658 2.675-.306 3.614-1.217.938-.911-.757-1.317-2.306-2.277-1.549-.958-1.72.4-1.72.4-1.038 2.597-.67 1.646.412 3.094ZM21.069 18.473c-1.093-1.186-.413-1.664-2.714-2.413-.63-.205-1.35-.256-1.61.447-.65 1.753 1.509 2.638 2.77 3.278.774.666 2.117-.7 1.552-1.313h.002Z"
            />
            <path
                fill={color}
                d="M9.08 24.866c.059-.018.298-.075 1.743-.522 1.288-.397.446-.958-.344-.782-.922.204-.873-.372-.873-.372.197-.822-.413-1.322-.859-1.078-.505.278-.671-.093-.81-.234-.076-.079-.302-1.336-.302-1.336a.502.502 0 0 0-.056-.097c-.45-.588-.71.09-.71.09l-.182.69-.189 1.07-.212.817c-.573 1.681.17 1.07.17 1.07l-.008.044c-1.782 2.965.992.752.992.752-.203 1.206.554.568.876.294.288-.246.442-.306.764-.407ZM15.486 15.11a6.528 6.528 0 0 1-.508-.45c-.054-.051-.121-.144-.375-.345-.158-.124-.42-.195-.609-.24a4.23 4.23 0 0 0-.45-.089c-.313-.04-.648-.019-.877.22a.716.716 0 0 0-.19.449l-.245.809c-.205 1.427 2.368 2.349 3.144 2.46.206.03.362-.091.465-.23.124-.169.144-.25.185-.45.051-.258.09-.52.078-.781a5.029 5.029 0 0 0-.078-.729c-.057-.295-.315-.452-.54-.626v.002ZM8.741 13.553c-1.487 2.024-.438 2.517-.018 2.262.537-.329 1.051-.032 1.051-.032 1.174.675 1.435.227 1.4-.178-.034-.407.831-1.247.831-1.247.86-1.051-.95-.443-1.879-1.056s-1.384.25-1.384.25h-.001ZM17.797 14.936c1.547-.56.699-1.047.832-1.442.156-.456-.267-1.266-.885-1.221-.559.04-.533-.63-.533-.63-.004-.217.606-.676-.808-.857-.835-.104-1.232.334-1.414.71-.127.26-.279.505-.279.505-.083.135.013.011-.192.266-.972 1.211 1.457 2.41 1.708 2.56.413.252.628.453 1.57.11ZM22.651 16.746c.461-1.377.059-1.884-.333-2.671-.393-.786-.087-.988-1.706-.968-1.619.019-1.47 1.366-1.47 1.366-.231 1.466.933 1.731 1.72 2.933.541.825 1.286.846 1.79-.66ZM19.14 12.588c1.075.405 1.886-.668 2.344-1.557.722-1.397-.703-.95-2.057-1.674-1.335-.714-1.6-.006-1.6-.006s-.747.666.17 1.955c.08.112.351.544.476.647.344.285.387.53.665.635h.001ZM13.685 8.69c-1.99 1.295-.48 1.746-.48 1.746.208.115 1.976.603 2.013-.012.037-.613.786-.575.786-.575 1.339.438 1.532-1.241 1.532-1.241.285-1.251-1.86-1.212-3.851.082ZM12.544 10.779c-.809-.768-1.7-.032-1.7-.032-1.671 1.049-.967 1.839.392 2.218 1.443.403 1.473.41 2.373-.478.901-.89.19-1.665-.243-1.466-.433.2-.82-.242-.82-.242h-.002ZM19.151 7.59c-1.906.507-.337.91-.337.91.876.04 1.954.504 1.954.504 1.377 1.133 1.384.513 1.393.359.01-.153-1.103-2.278-3.01-1.772ZM22.98 11.505c-.383-1.32-.972-.125-.972-.125-.262.318-.243.901.5 2.082.9 1.429.894-.502.472-1.957Z"
            />
            <path
                fill={color}
                d="M6.086 16.048c.098 1.77.696.117.696.117s1.016-2.511 1.492-3.354c.478-.842 1.848-2.544 3.585-3.842.367-.272.756-.52 1.162-.732.605-.314 1.247-.549 1.903-.74.636-.185 1.279-.391 1.925-.543a7.387 7.387 0 0 1 1.455-.174c.5-.018 1.008-.028 1.5.088.199.048.397.103.593.163.139.043.62.207.719.016.12-.232-.422-.494-.556-.569a5.641 5.641 0 0 0-1.078-.467 2.988 2.988 0 0 0-.67-.112c-.835-.056-1.668.073-2.5.137-.184.013-.372.066-.553.097-.702.12-1.375.339-2.04.562a7.64 7.64 0 0 0-.87.366c-.978.474-2 .953-2.857 1.622a38.443 38.443 0 0 0-2.699 2.35 4.27 4.27 0 0 0-.616.719c-.241.358-.44.76-.53 1.183-.083.38-.083.746-.07 1.133l.002.116c-.002.272-.034.54-.03.812.002.35.018.702.038 1.052h-.001ZM24.406 17.104c.12-.333.236-.666.33-1.007.037-.132.075-.264.107-.396.1-.407.123-.88.133-1.299.006-.304-.017-.812-.202-1.064-.078-.108-.435-.526-.596-.393-.11.09-.14.31-.167.437a3.88 3.88 0 0 0-.032.18c-.127.773-.06 1.561-.289 2.324-.1.335-.228.66-.355.985-.159.404-.282.818-.485 1.202-.448.851-1.016 1.795-2.436 3.33-.36.387-.737.755-1.15 1.087-.518.415-1.098.719-1.688 1.023-.88.453-1.812.752-2.75 1.06-.154.05-.965.324-.472.477.296.093.61.088.914.108.316.021.63.047.947.07.55.04 1.032.018 1.532-.239.512-.264.95-.634 1.427-.947.295-.192.58-.396.856-.613.385-.301.753-.628 1.083-.986.346-.371.639-.783.95-1.182.865-1.106 1.144-1.685.914-1.291-.226.39.444-.787.635-1.197.183-.396.647-1.262.792-1.671l.002.002ZM28.829 17.371s-1.098-1.466-2.027-2.733c-.157-.215-.36-.486-.622-.581-.3-.108-.521.044-.662.3a1.39 1.39 0 0 0-.033.064c-.287.578-.235 1.21-.156 1.831.205 1.611-.413 2.387-.413 2.387s-1.205 1.557-.51 2.391c.697.833.114 2.553.114 2.553-.07.2-.078.468-.072.679.007.231.036.474.214.642.158.151.404.233.618.256.378.043.72-.064 1.04-.26.308-.185.563-.444.819-.69.28-.268.492-.589.737-.892.62-.769 1.066-1.402 1.232-2.231.164-.828.177-.67.267-1.364.06-.46-.098-1.502-.273-1.941-.065-.166-.172-.274-.274-.412v.001Zm-3.183 4.4s-.033-.726-.221-1.07c0 0 .299-1.702.922-.544.622 1.157-.7 1.613-.7 1.613Zm2.378-1.102c-.369.432-.752-.463-.864-.707-.69-1.493-.787-2.04-.787-2.04s-.295-.917.257-.37c.396.394.661.952.92 1.437.208.388.864 1.223.474 1.68ZM7.728 5.144c.312.194.918.22 1.267.129.262-.071.511-.18.776-.246.79-.193 1.709-.079 2.205.624.235.33.603.724 1.05.711.4-.01.727-.269 1.065-.448.355-.188.738-.34 1.12-.442.75-.2 1.54-.282 2.316-.25.313.013.62.05.929.102.345.06.822.154 1.165.04.723-.24-.547-1.394-.758-1.582-.4-.359-.82-.702-1.267-1.003a8.055 8.055 0 0 0-2.611-1.142c-.787-.192-1.604-.331-2.418-.352-.941-.024-2.357.296-4.28 1.577a3.84 3.84 0 0 0-.687.555c-.203.219-.403.488-.418.792-.012.247.128.533.318.74.07.08.148.145.228.195Zm6.455-2.697c1.368.649 1.774.98 1.774.98s.762.475.078.412c-.492-.043-1.007-.282-1.471-.473-.373-.153-1.84-.363-1.893-.916-.05-.525 1.289-.11 1.512-.004Zm-.453 1.702c.958.908-.786.722-.786.722-.285-.274-.984-.506-.984-.506s.812-1.125 1.77-.216ZM30.412 5.76a5.52 5.52 0 0 0-.671-.446c-.111-.064-.241-.114-.348-.187-.079-.053-.18-.084-.27-.114-.287-.097-.58-.173-.873-.25a7.427 7.427 0 0 0-1.156-.184c-.58-.05-1.114.077-1.67.213a4.069 4.069 0 0 0-.701.235c-.29.131-.565.295-.796.515-.08.077-.152.164-.217.25-.089.117-.186.228-.261.352-.02.032-.056.116-.08.145-.084.098-.125.15-.215.243-.14.15-.276.302-.404.463-.239.298-.469.7-.528 1.083-.047.288.052.572.223.806.175.237.449.378.621.616.223.304.344.708.45 1.063.066.217.127.436.175.657a1.82 1.82 0 0 0 .11.68c.09.22.28.43.526.48.367.077.495-.274.646-.485.124-.173.214-.318.4-.422.174-.096.348-.087.538-.09.141-.003.289-.004.429-.023.53-.069 1.08-.39 1.533-.662.357-.215.759-.483.973-.85.098-.166.23-.313.369-.448a6.74 6.74 0 0 1 .884-.705c.324-.222.384-.367.619-.681.13-.176.23-.421.262-.638.087-.614-.105-1.243-.568-1.613V5.76Zm-5.501 1.352c.662-.826 2.161-.752 2.161-.752-1.345.059-1.703.933-1.703.933-1.072.814-.46-.181-.46-.181h.002Zm.364.654c.798-.978 1.598-.896 1.598-.896-.438-.02-.947 1.064-.947 1.064-1.22.9-.651-.168-.651-.168ZM27.5 9.15s-.617.059-.605.374c.01.314-.864.44-.472-.37.282-.583 1.127-.427 1.127-.427s1.089.187-.05.423Z"
            />
        </Icon>
    )
}

export const UltraFastDeliveryIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 34 34" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                d="M12.671 16.996c.858 2.407 2.298 4.925 4.32 7.555 2.03-2.619 3.477-5.128 4.341-7.529-.952-.537-4.847-2.356-8.66-.026ZM16.992 1.989a12.614 12.614 0 0 0-4.69 6.219c4.117-2.79 7.809-1.041 9.38 0a12.598 12.598 0 0 0-4.69-6.22Z"
            />
            <path
                fill="currentColor"
                d="M11.516 8.563a12.961 12.961 0 0 1 4.405-6.598c-4.657.327-8.565 2.854-10.209 6.294 2.337-1.1 4.861-.137 5.804.304ZM22.085 9.715c-.033-.17-.07-.327-.105-.497-.622-.537-4.962-3.97-9.957 0-.036.16-.072.327-.101.487-.377 2.231-.192 4.52.54 6.661 3.927-2.31 7.885-.605 9.08.017a13.67 13.67 0 0 0 .543-6.668ZM22.48 8.527c2.256-1.309 4.61-.733 5.8-.284-1.646-3.434-5.547-5.95-10.199-6.278a12.958 12.958 0 0 1 4.4 6.562ZM12.023 17.134c-1.198-.252-3.892-.56-4.978.41.397.473.835.911 1.309 1.308.187.17 4.31 3.853 5.171 5.892h2.812c-2.01-2.63-3.448-5.166-4.314-7.61Z"
            />
            <path
                fill="currentColor"
                d="M22.645 9.19c.026.132.055.263.082.394a14.213 14.213 0 0 1-.498 6.83c3.028-.605 4.553.18 5.142.632.311-.42.586-.868.822-1.335a8.314 8.314 0 0 0 .448-6.592c-.59-.282-3.44-1.516-5.996.072ZM11.794 16.424a14.234 14.234 0 0 1-.514-6.838c.023-.124.05-.245.075-.366-.798-.38-3.715-1.6-6.042.03a8.317 8.317 0 0 0-.432 2.618 8.605 8.605 0 0 0 1.755 5.155c1.305-1.116 3.99-.802 5.158-.6Z"
            />
            <path
                fill="currentColor"
                d="M21.98 17.133c-.858 2.438-2.291 4.985-4.317 7.623h2.801c.861-2.053 4.985-5.735 5.165-5.892.473-.399.911-.837 1.31-1.31-.485-.36-1.935-1.115-4.96-.421Z"
            />
            <path
                fill="currentColor"
                d="M19.63 27.788h-5.256a.327.327 0 0 1-.316-.239.327.327 0 0 1-.008-.14l.494-3.031a.327.327 0 0 1 .327-.275h4.268a.327.327 0 0 1 .328.275l.494 3.03a.328.328 0 0 1-.327.38h-.004Zm-4.873-.654h4.49l-.386-2.377h-3.722l-.382 2.377Z"
            />
            <path
                fill="currentColor"
                d="M21.132 27.46v3.194a1.384 1.384 0 0 1-1.381 1.382h-5.499a1.387 1.387 0 0 1-1.385-1.382V27.46a.327.327 0 0 1 .328-.328h7.61a.327.327 0 0 1 .327.328Z"
            />
        </Icon>
    )
}

export const UserIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 14" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.1"
                d="M5.449 5.9a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z"
                clipRule="evenodd"
            />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.1"
                d="M1.049 12.5v-2.2a2.2 2.2 0 0 1 2.2-2.2h4.4c.4 0 .776.107 1.1.294"
            />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.1"
                d="M10.948 10.3h-5.5M9.848 12.5h-4.4"
            />
        </Icon>
    )
}

export const VisibilityIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 20 20" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M.833 10S4.167 3.333 10 3.333c5.834 0 9.167 6.667 9.167 6.667S15.833 16.667 10 16.667.833 10 .833 10Z"
            />
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
            />
        </Icon>
    )
}

export const VisibilityOffIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 16 16" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <g
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.2"
                clipPath="url(#a)"
            >
                <path d="M11.959 11.96a6.714 6.714 0 0 1-3.96 1.373C3.332 13.333.665 8 .665 8A12.3 12.3 0 0 1 4.04 4.04m2.56-1.213a6.08 6.08 0 0 1 1.4-.16C12.665 2.667 15.332 8 15.332 8c-.405.757-.887 1.47-1.44 2.127m-4.48-.714a2 2 0 1 1-2.827-2.826M.665.667l14.667 14.666" />
            </g>
            <defs>
                <clipPath id="a">
                    <path fill="#fff" d="M-.001 0h16v16h-16z" />
                </clipPath>
            </defs>
        </Icon>
    )
}

export const WhatsappIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Icon title',
            id: 'icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Icon description',
            id: 'icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 32 32" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <circle cx="16" cy="16" r="15.75" stroke="#D9D9D9" strokeWidth=".5" />
            <path
                stroke="currentColor"
                strokeLinejoin="round"
                d="M18.833 19c-1.16 1.16-3.832.171-5.168-1.165-1.336-1.336-2.325-4.008-1.165-5.168.336-.336.799-.376 1.081-.363a.53.53 0 0 1 .4.237l.635.889a.667.667 0 0 1-.071.859l-.6.6c.11.334.466 1.134 1 1.667.532.533 1.332.89 1.666 1l.6-.6a.667.667 0 0 1 .86-.072l.888.635a.53.53 0 0 1 .237.4c.013.282-.027.745-.363 1.081Z"
            />
            <path
                stroke="currentColor"
                strokeLinejoin="round"
                d="M15.5 23a7 7 0 1 0-6.063-3.5l-.587 3.15 3.15-.587a6.968 6.968 0 0 0 3.5.937Z"
            />
        </Icon>
    )
}

export const WishlistIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Add to favourites',
            id: 'wishlist.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Add to favourites icon',
            id: 'wishlist.icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                d="M1.999 12V1.295c0-.362.107-.668.323-.919C2.537.126 2.8 0 3.11 0h5.775c.311 0 .574.125.79.376.215.25.323.557.323.92V12l-4-1.998-4 1.998Zm.923-1.648 3.077-1.54 3.076 1.54V1.295a.228.228 0 0 0-.059-.151c-.04-.046-.083-.07-.13-.07H3.111c-.047 0-.09.024-.13.07a.228.228 0 0 0-.06.151v9.057Z"
            />
        </Icon>
    )
}

export const WishlistFilledIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Remove from favourites',
            id: 'wishlistfilled.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Remove from favourites icon',
            id: 'wishlistfilled.icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 12 12" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                fill="currentColor"
                d="M1.999 12V1.295c0-.362.107-.668.323-.919C2.537.126 2.8 0 3.11 0h5.775c.311 0 .574.125.79.376.215.25.323.557.323.92V12l-4-1.998-4 1.998z"
            />
        </Icon>
    )
}

export const AccountIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Account icon',
            id: 'account.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Account icon',
            id: 'account.icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 14 17" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M6.67 7.79a3.52 3.52 0 1 0 0-7.04 3.52 3.52 0 0 0 0 7.04"
                clipRule="evenodd"
            />
            <path stroke="currentColor" strokeWidth="1.5" d="M12.75 15.75h-12a6 6 0 0 1 12 0Z" />
        </Icon>
    )
}

export const AccountRegisteredIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Account icon',
            id: 'account.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Account icon',
            id: 'account.icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 18 19" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M6.67 7.79a3.52 3.52 0 1 0 0-7.04 3.52 3.52 0 0 0 0 7.04"
                clipRule="evenodd"
            />
            <path
                stroke="currentColor"
                strokeWidth="1.5"
                d="M12.75 15.75a6 6 0 0 0-12 0l11.963-.062"
            />
            <circle
                cx="13.176"
                cy="14.125"
                r="3.5"
                fill="#36af26"
                stroke="transparent"
                strokeWidth="2"
            />
        </Icon>
    )
}

export const AccountRegisteredWhiteIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Account icon',
            id: 'account.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Account icon',
            id: 'account.icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 18 19" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M6.67 7.79a3.52 3.52 0 1 0 0-7.04 3.52 3.52 0 0 0 0 7.04"
                clipRule="evenodd"
            />
            <path
                stroke="currentColor"
                strokeWidth="1.5"
                d="M12.75 15.75a6 6 0 0 0-12 0l11.963-.062"
            />
            <circle cx="13.176" cy="14.125" r="3.5" fill="#36af26" stroke="#fff" strokeWidth="2" />
        </Icon>
    )
}

export const CartIcon = ({svgTitle, svgDescription, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Cart icon',
            id: 'cart.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Cart icon',
            id: 'cart.icon.svg.description.default'
        })

    return (
        <Icon viewBox="0 0 16 17" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="#090909" strokeWidth="1.36" d="M1.18 4.86h13.64V15.5H1.18z" />
            <path
                stroke="#090909"
                strokeWidth="1.36"
                d="M5.5 5.439V2.964c0-3.063 5.524-3.028 5.524 0V5.44"
            />
        </Icon>
    )
}

export const CartFilledIcon = ({svgTitle, svgDescription, productCount = 0, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Cart icon',
            id: 'cart.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Cart icon',
            id: 'cart.icon.svg.description.default'
        })

    const displayCount = productCount > 99 ? '99+' : String(productCount)

    return (
        <Icon viewBox="0 0 26 24" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="#090909" strokeWidth="1.36" d="M.68 4.86h13.64V15.5H.68z" />
            <path
                stroke="#090909"
                strokeWidth="1.36"
                d="M5 5.439V2.964c0-3.063 5.524-3.028 5.524 0V5.44"
            />
            <rect
                width="21.133"
                height="16.133"
                x="3.433"
                y="6.813"
                fill="#000"
                stroke="#fff"
                strokeWidth="1.133"
                rx="8.067"
            />
            <rect
                width="21.133"
                height="16.133"
                x="3.433"
                y="6.813"
                fill="#000"
                stroke="transparent"
                strokeWidth="1.133"
                rx="8.067"
            />
            <text
                x="14"
                y="15.4"
                fill="#fff"
                fontSize={displayCount.length === 1 ? '13' : displayCount.length === 2 ? '11' : '8'}
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
            >
                {displayCount}
            </text>
        </Icon>
    )
}

export const CartFilledWhiteIcon = ({svgTitle, svgDescription, productCount = 0, ...props}) => {
    const {formatMessage} = useIntl()
    const title =
        svgTitle ||
        formatMessage({
            defaultMessage: 'Cart icon',
            id: 'cart.icon.svg.title.default'
        })
    const description =
        svgDescription ||
        formatMessage({
            defaultMessage: 'Cart icon',
            id: 'cart.icon.svg.description.default'
        })

    const displayCount = productCount > 99 ? '99+' : String(productCount)

    return (
        <Icon viewBox="0 0 26 24" fill="none" {...props}>
            <title>{title}</title>
            <desc>{description}</desc>
            <path stroke="#fff" strokeWidth="1.36" d="M.68 4.86h13.64V15.5H.68z" />
            <path
                stroke="#fff"
                strokeWidth="1.36"
                d="M5 5.439V2.964c0-3.063 5.524-3.028 5.524 0V5.44"
            />
            <rect
                width="21.133"
                height="16.133"
                x="3.433"
                y="6.813"
                fill="#000"
                stroke="#fff"
                strokeWidth="1.133"
                rx="8.067"
            />
            <rect
                width="21.133"
                height="16.133"
                x="3.433"
                y="6.813"
                fill="#fff"
                stroke="#090909"
                strokeWidth="1.133"
                rx="8.067"
            />
            <text
                x="14"
                y="16"
                fill="#090909"
                fontSize={displayCount.length === 1 ? '13' : displayCount.length === 2 ? '11' : '8'}
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
            >
                {displayCount}
            </text>
        </Icon>
    )
}

RadioIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

CheckedRadioIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

AlertIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

Alert2Icon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string,
    insideColor: PropTypes.string
}

BasketIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

BrandLogo.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

BrandLogoWhite.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

BookIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

CheckIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

ChevronUpIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

ChevronDownIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

ChevronLeftIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

ChevronRightIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

CloseIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

CubeIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

CustomerServiceIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

DeliveryIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

EasyReturnsIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

FilterIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

Filter2Icon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

FlagFRIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

FlagUSIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

GiftIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

HamburgerIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

InfoIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

LoaderIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

LocationIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

PinIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

MinusIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

NoLocationIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

OrdersIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

PhoneIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

PlusIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

PrivacyIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

RefreshIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

RepairIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

RightArrowIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

SearchIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

SecurePaymentIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

SecurePaymentsIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

ShopIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

StoreVBQIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

StoreOutletIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

StoreOthersIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

StoreMarkerIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

SuccessIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

TurtleIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string,
    color: PropTypes.string
}

UltraFastDeliveryIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

UserIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

VisibilityIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

VisibilityOffIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

WhatsappIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

WishlistIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

WishlistFilledIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

AccountIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

AccountRegisteredIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

AccountRegisteredWhiteIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

CartIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string
}

CartFilledIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string,
    productCount: PropTypes.number
}

CartFilledWhiteIcon.propTypes = {
    svgTitle: PropTypes.string,
    svgDescription: PropTypes.string,
    productCount: PropTypes.number
}
